import {
  EnumerationItem,
  SelectableEnumerationItem,
} from "../models/lookup.model";
import { DateTime } from "luxon";
import { EventInput } from "@fullcalendar/core";
import { EncounterSchedule } from "../models/procedure.model";
import {
  EncounterFamilyHistory,
  EncounterMedicalHistory,
  EncounterMedication,
  EncounterSocialHistory,
  EncounterSurgicalHistory,
  Problem,
  ProblemService,
} from "../models/problems.model";
import { Context } from "../models/contexts.model";
import { Prompt } from "../models/prompts.model";
import { Template } from "../models/templates.model";
import { Allergy } from "../models/allergy.model";
import { isCancelledEncounterMedication } from "../helpers/no-known-medications.util";
import { filterEncounterContextsForSoapNote } from "../helpers/last-indicated-context-match.util";
import { jsonrepair } from "jsonrepair";
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';

export class Utils {
  /**
   * Highlights search terms in text with yellow background
   * Supports multi-word search - each word is highlighted separately
   * @param text - The full text to search in
   * @param searchTerm - The term to highlight (can be multiple words)
   * @param sanitizer - Angular DomSanitizer instance
   * @returns SafeHtml with highlighted search term(s)
   */
  static highlightSearchTerm(text: string, searchTerm: string, sanitizer: DomSanitizer): SafeHtml {
    if (!searchTerm || !text) {
      return sanitizer.sanitize(1, text) || text;
    }

    // Split search term into individual words and filter out empty strings
    const searchWords = searchTerm.trim().split(/\s+/).filter(word => word.length > 0);
    
    if (searchWords.length === 0) {
      return sanitizer.sanitize(1, text) || text;
    }

    // Escape special regex characters for each word
    const escapedWords = searchWords.map(word => 
      word.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
    );
    
    // Create regex pattern that matches any of the words (case-insensitive)
    const regexPattern = escapedWords.join('|');
    const regex = new RegExp(`(${regexPattern})`, 'gi');
    
    // Replace matches with highlighted mark tag
    const highlightedText = text.replace(regex, (match) => {
      return `<mark style="background-color: #fff3cd; font-weight: 600; padding: 2px 0;">${match}</mark>`;
    });
    
    // Bypass Angular's security to allow inline styles
    return sanitizer.bypassSecurityTrustHtml(highlightedText);
  }

  static convertDateToHHmmFormat(date: Date) {
    if (!date) {
      return null;
    }
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");
    return `${hours}:${minutes}`;
  }

  /** Parses manual time entry (e.g. `1212` → 12:12) for time-only calendar inputs. */
  static parseTypedTimeToDate(
    value: string | null | undefined,
    fallbackDate?: Date | null
  ): Date | null {
    const raw = String(value ?? "").trim();
    if (!raw) {
      return null;
    }

    for (const format of ["HH:mm", "H:mm", "hh:mm a", "h:mm a", "HH:mm:ss"]) {
      const lux = DateTime.fromFormat(raw, format);
      if (lux.isValid) {
        return lux.toJSDate();
      }
    }

    const digits = raw.replace(/\D/g, "");
    if (/^\d{3,4}$/.test(digits)) {
      let hours: number;
      let minutes: number;
      if (digits.length === 4) {
        hours = parseInt(digits.slice(0, 2), 10);
        minutes = parseInt(digits.slice(2, 4), 10);
      } else {
        hours = parseInt(digits.slice(0, 1), 10);
        minutes = parseInt(digits.slice(1, 3), 10);
      }
      if (
        Number.isFinite(hours) &&
        Number.isFinite(minutes) &&
        hours >= 0 &&
        hours <= 23 &&
        minutes >= 0 &&
        minutes <= 59
      ) {
        const base =
          fallbackDate && !Number.isNaN(fallbackDate.getTime())
            ? DateTime.fromJSDate(fallbackDate)
            : DateTime.now();
        return base
          .set({ hour: hours, minute: minutes, second: 0, millisecond: 0 })
          .toJSDate();
      }
    }

    return null;
  }

  static removeUnderscoresFromString(input: string) {
    if (!input) {
      return "";
    }

    const toTitleCase = (str) =>
      str
        .split("_")
        .map(
          (word: string) =>
            word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
        )
        .join(" ");

    const [rawMainType = "", rawSubType = ""] = input.split(".");
    return [toTitleCase(rawMainType), toTitleCase(rawSubType)];
  }

  static parseJwt(token: string): any {
    let base64Url = token.split(".")[1];
    let base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    let jsonPayload = decodeURIComponent(
      atob(base64)
        .split("")
        .map((c) => {
          return "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2);
        })
        .join("")
    );

    return JSON.parse(jsonPayload);
  }

  static generateUniqueValue(): string {
    const timestamp = Date.now();
    const random = Math.random().toString(36).substring(2, 8); // Random string
    return `-${timestamp}-${random}`;
  }

  static extractFileKeyFromUrl(url: string): string {
    const parts = url.split("s3.amazonaws.com/");
    if (parts.length !== 2) {
      console.error("Invalid file url: ", url);
    }
    return parts[1];
  }

  static getTokenExpireDate(exp: number) {
    let expiresDate = new Date(0);
    expiresDate.setUTCMilliseconds(exp * 1000);

    return expiresDate;
  }

  static roundNumber(value: number): number {
    const threshold: number = 0.1;
    const decimalPart = value % 1;

    if (Math.abs(decimalPart) < threshold) {
      return Math.round(value); // Return as an integer
    }

    return parseFloat(value.toFixed(2));
  }

  static parseLocalDate(dateString: string) {
    const [year, month, day] = dateString.split("-").map(Number);
    return new Date(year, month - 1, day);
  }

  static calculateAge(dob: string): number {
    if (!dob) return 0;
    
    let birthDate: Date;
    try {
      // Handle different date formats
      if (dob.includes('/')) {
        // Format: MM/DD/YYYY or M/D/YYYY
        const parts = dob.split('/');
        if (parts.length === 3) {
          const month = parseInt(parts[0], 10);
          const day = parseInt(parts[1], 10);
          const year = parseInt(parts[2], 10);
          birthDate = new Date(year, month - 1, day);
        } else {
          birthDate = new Date(dob);
        }
      } else if (dob.includes('-')) {
        // Format: YYYY-MM-DD (ISO format)
        const parts = dob.split('-');
        if (parts.length === 3) {
          const year = parseInt(parts[0], 10);
          const month = parseInt(parts[1], 10);
          const day = parseInt(parts[2], 10);
          birthDate = new Date(year, month - 1, day);
        } else {
          birthDate = new Date(dob);
        }
      } else if (dob.includes('T')) {
        // ISO datetime format: 2024-12-25T00:00:00.000Z
        birthDate = new Date(dob);
        // Adjust for timezone to get local date
        birthDate = new Date(birthDate.getTime() + birthDate.getTimezoneOffset() * 60000);
      } else {
        birthDate = new Date(dob);
      }
      
      if (isNaN(birthDate.getTime())) {
        return 0;
      }
    } catch (error) {
      return 0;
    }

    // Get today's date
    const today = new Date();

    // Calculate the age
    let age = today.getFullYear() - birthDate.getFullYear();

    // Adjust age if the birthday hasn't occurred yet this year
    const monthDifference = today.getMonth() - birthDate.getMonth();
    const dayDifference = today.getDate() - birthDate.getDate();
    if (monthDifference < 0 || (monthDifference === 0 && dayDifference < 0)) {
      age--;
    }

    return age;
  }

  static splitCamelCase(value: string | undefined): string {
    return value?.replace(/([a-z])([A-Z])/g, "$1 $2") ?? "";
  }

  static getAlphabetLabel(index: number): string {
    const alphabet = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
    if (index < 26) {
      return alphabet[index];
    } else {
      const first = alphabet[Math.floor(index / 26) - 1];
      const second = alphabet[index % 26];
      return first + second;
    }
  }

  static getInitialsFromFullName(fullName: string) {
    const nameArray = fullName.split(" ");
    const initials = nameArray
      .map((name) => name.charAt(0).toUpperCase())
      .join("");
    return initials;
  }

  static toTitleCase(value: string): string {
    if (!value) {
      return "";
    }
    return value.replace(/\w\S*/g, (txt) => {
      return txt.charAt(0).toUpperCase() + txt.substring(1).toLowerCase();
    });
  }

  static formatISODateWithoutTime(date: Date) {
    let month: number | string = date.getMonth() + 1;
    if (month < 10) {
      month = "0" + month;
    }
    let day: number | string = date.getDate();
    if (day < 10) {
      day = "0" + day;
    }
    return date.getFullYear() + "-" + month + "-" + day + "T00:00:00";
  }

  static getStartOfDayISODate(date: Date): string {
    let month: number | string = date.getMonth() + 1;
    if (month < 10) {
      month = "0" + month;
    }
    let day: number | string = date.getDate();
    if (day < 10) {
      day = "0" + day;
    }
    return date.getFullYear() + "-" + month + "-" + day + "T00:00:00";
  }

  static getEndOfDayISODate(date: Date): string {
    let month: number | string = date.getMonth() + 1;
    if (month < 10) {
      month = "0" + month;
    }
    let day: number | string = date.getDate();
    if (day < 10) {
      day = "0" + day;
    }
    return date.getFullYear() + "-" + month + "-" + day + "T23:59:59";
  }

  static getStartOfWeek() {
    return DateTime.now().startOf("week").startOf("day").toJSDate();
  }

  static getEndOfWeek() {
    return DateTime.now().endOf("week").endOf("day").toJSDate();
  }
  static getStartOfMonth() {
    return DateTime.now().startOf("month").startOf("day").toJSDate();
  }

  static getEndOfMonth() {
    return DateTime.now().endOf("month").endOf("day").toJSDate();
  }

  static MinsToHm = (minsFromMidNight: number): string => {
    minsFromMidNight = Number(minsFromMidNight);
    const h = Math.floor(minsFromMidNight / 60);
    const m = minsFromMidNight % 60;
    const formattedHours =
      h.toString().length === 1 ? "0" + h.toString() : h.toString();
    const formattedMins =
      m.toString().length === 1 ? "0" + m.toString() : m.toString();
    return `${formattedHours}:${formattedMins}`;
  };

  static HmToMins = (formattedTime: string): number => {
    const hmString = formattedTime.split(":");
    if (hmString.length !== 2) {
      return 0;
    }
    const hrs = Number(hmString[0]);
    const mins = Number(hmString[1]);
    return hrs * 60 + mins;
  };

  static getTimeInMinutesFromMidnight(date: Date): number {
    if (!(date instanceof Date)) {
      return 0;
    }

    const hours = date.getHours();
    const minutes = date.getMinutes();

    return hours * 60 + minutes;
  }

  static formatPhoneNumber = (number: string): string => {
    let cleaned = ("" + number).replace(/\D/g, ""),
      match = cleaned.match(/^(1|)?(\d{3})(\d{3})(\d{3}|\d{4})$/);
    if (match) {
      let intlCode = match[1] ? "+1 " : "";
      return ["(", match[2], ") ", match[3], "-", match[4]].join("");
    }
    return number;
  };

  static getEnumerationItemsFromDayOfWeekEnum() {
    return Object.keys(DayOfWeek).map((key) => ({
      value: DayOfWeek[key],
      displayName: key,
    }));
  }

  static getEnumerationItemsFromMaritalStatusEnum() {
    return Object.keys(MaritalStatus).map((key) => ({
      value: MaritalStatus[key],
      displayName: key,
    }));
  }

  static getEnumerationItemsFromMedicationStatusEnum() {
    return Object.keys(MedicationStatus).map((key) => ({
      value: MedicationStatus[key],
      displayName: key,
    }));
  }

  static getEnumerationItemsFromGenderEnum() {
    return Object.keys(Gender).map((key) => ({
      value: Gender[key],
      displayName: key,
    }));
  }

  static getEnumerationItemsFromEthnicityEnum() {
    return Object.keys(Ethnicity).map((key) => ({
      value: Ethnicity[key],
      displayName: key,
    }));
  }

  static getEnumerationItemsFromRelationshipEnum() {
    return Object.keys(Relationship).map((key) => ({
      value: key,
      displayName: Relationship[key],
    }));
  }

  static getEnumerationItemsFromEncounerStatusEnum(): SelectableEnumerationItem[] {
    return Object.keys(EncounterStatusV2).map((key) => ({
      value: EncounterStatusV2[key],
      displayName: EncounterStatusV2[key],
      isSelected: false,
    }));
  }

  static getEnumerationItemsFromVisitTypeEnum(): SelectableEnumerationItem[] {
    return Object.keys(VisitType).map((key) => ({
      value: VisitType[key].toLowerCase(),
      displayName: VisitType[key],
      isSelected: false,
    }));
  }

  static getEnumerationItemsFromUploadDocumentTypeEnum() {
    return Object.keys(UploadDocumentType).map((key) => ({
      value: UploadDocumentType[key],
      displayName: UploadDocumentType[key],
    }));
  }

  static getEnumerationItemsFromRegionEnum() {
    return Object.keys(Region).map((key) => ({
      value: Region[key],
      displayName: Region[key],
    }));
  }

  static getEnumerationItemsFromAreaEnum() {
    return Object.keys(Area).map((key) => ({
      value: Area[key],
      displayName: Area[key],
    }));
  }

  static getEnumerationItemsFromSideEnum() {
    return Object.keys(Side).map((key) => ({
      value: Side[key],
      displayName: Side[key],
    }));
  }

  static getCptCodesForType(type: CptCodeType) {
    switch (type) {
      case CptCodeType.Carotid:
        return this.getCaroidCptCodes();
      case CptCodeType.Artery:
        return this.getArteryCptCodes();
      case CptCodeType.Vein:
        return this.getVeinCptCodes();
    }
  }

  static getEnumerationItemsFromLocationEnum(
    selectedRegion: Region | undefined
  ) {
    return [
      {
        value: Location.DistalAbdominal,
        displayName: Location.DistalAbdominal,
        parentValue: Region.LowerExtremity,
      },
      {
        value: Location.CommonIliac,
        displayName: Location.CommonIliac,
        parentValue: Region.LowerExtremity,
      },
      {
        value: Location.ExternalIliac,
        displayName: Location.ExternalIliac,
        parentValue: Region.LowerExtremity,
      },
      {
        value: Location.InternalIliac,
        displayName: Location.InternalIliac,
        parentValue: Region.LowerExtremity,
      },
      {
        value: Location.DeepFemoralProfunda,
        displayName: Location.DeepFemoralProfunda,
        parentValue: Region.LowerExtremity,
      },
      {
        value: Location.CommonFemoral,
        displayName: Location.CommonFemoral,
        parentValue: Region.LowerExtremity,
      },
      {
        value: Location.ProxSuperficialFemoral,
        displayName: Location.ProxSuperficialFemoral,
        parentValue: Region.LowerExtremity,
      },
      {
        value: Location.MidSuperficialFemoral,
        displayName: Location.MidSuperficialFemoral,
        parentValue: Region.LowerExtremity,
      },
      {
        value: Location.DisSuperficialFemoral,
        displayName: Location.DisSuperficialFemoral,
        parentValue: Region.LowerExtremity,
      },
      {
        value: Location.Popliteal,
        displayName: Location.Popliteal,
        parentValue: Region.LowerExtremity,
      },
      {
        value: Location.AnteriorTibial,
        displayName: Location.AnteriorTibial,
        parentValue: Region.LowerExtremity,
      },
      {
        value: Location.TibialPeronealTrunk,
        displayName: Location.TibialPeronealTrunk,
        parentValue: Region.LowerExtremity,
      },
      {
        value: Location.PosteriorTibial,
        displayName: Location.PosteriorTibial,
        parentValue: Region.LowerExtremity,
      },
      {
        value: Location.Peroneal,
        displayName: Location.Peroneal,
        parentValue: Region.LowerExtremity,
      },
      {
        value: Location.DorsalisPedis,
        displayName: Location.DorsalisPedis,
        parentValue: Region.LowerExtremity,
      },
      {
        value: Location.Planter,
        displayName: Location.Planter,
        parentValue: Region.LowerExtremity,
      },
      {
        value: Location.Retroduodenal,
        displayName: Location.Retroduodenal,
        parentValue: Region.Biliary,
      },
      {
        value: Location.Gastroduodenal,
        displayName: Location.Gastroduodenal,
        parentValue: Region.Biliary,
      },
      {
        value: Location.RightBranchHepatic,
        displayName: Location.RightBranchHepatic,
        parentValue: Region.Biliary,
      },
      {
        value: Location.Cystic,
        displayName: Location.Cystic,
        parentValue: Region.Biliary,
      },
      {
        value: Location.CeliacTrunk,
        displayName: Location.CeliacTrunk,
        parentValue: Region.Biliary,
      },
      {
        value: Location.SuperiorMesenteric,
        displayName: Location.SuperiorMesenteric,
        parentValue: Region.Biliary,
      },
      {
        value: Location.InferiorMesenteric,
        displayName: Location.InferiorMesenteric,
        parentValue: Region.Biliary,
      },
      {
        value: Location.Radial,
        displayName: Location.Radial,
        parentValue: Region.UpperExtremity,
      },
      {
        value: Location.Ulnar,
        displayName: Location.Ulnar,
        parentValue: Region.UpperExtremity,
      },
      {
        value: Location.Brachial,
        displayName: Location.Brachial,
        parentValue: Region.UpperExtremity,
      },
      {
        value: Location.Brachialcephalic,
        displayName: Location.Brachialcephalic,
        parentValue: Region.UpperExtremity,
      },
      {
        value: Location.Subclavian,
        displayName: Location.Subclavian,
        parentValue: Region.UpperExtremity,
      },
    ].filter((c) => !selectedRegion || c.parentValue === selectedRegion);
  }
  static getEnumerationItemsFromProcedureTypeEnum() {
    return Object.keys(ProcedureType).map((key) => ({
      value: ProcedureType[key],
      displayName: ProcedureType[key],
    }));
  }

  static getEnumerationItemsForAccessSites() {
    return Object.keys(AccessSites).map((key) => ({
      value: AccessSites[key],
      displayName: AccessSites[key],
    }));
  }

  static formatAccessSiteDisplayLabel(site: {
    location?: string;
    accessSite?: string;
    siteType?: string;
  }): string {
    return [site.location, site.accessSite, site.siteType]
      .map((part) => String(part || "").trim())
      .filter(Boolean)
      .join(" ");
  }

  static getEnumerationItemsFromLabTestEnum() {
    return Object.keys(LabTest).map((key) => ({
      value: LabTest[key],
      displayName: LabTest[key],
    }));
  }

  static getEnumerationItemsFromProcedureDocumentsEnum() {
    return Object.keys(ProcedureDocuments).map((key) => ({
      value: ProcedureDocuments[key],
      displayName: ProcedureDocuments[key],
    }));
  }

  private static getCaroidCptCodes(): cptCodesForPriorAuth[] {
    return [
      {
        code: "36221",
        tooltip:
          "CPT 36221 - Selective catheter placement, arterial system; each first order abdominal, pelvic, or lower extremity artery branch, within a vascular family",
        section: CptCodeSection.Diagnostics,
      },
      {
        code: "36222",
        tooltip:
          "CPT 36222 - Selective catheter placement, arterial system; initial second order abdominal, pelvic, or lower section artery branch",
        section: CptCodeSection.Diagnostics,
      },
      {
        code: "36223",
        tooltip:
          "CPT 36223 - Selective catheter placement, arterial system; initial third order or more selective abdominal, pelvic, or lower extremity artery branch, within a vascular family",
        section: CptCodeSection.Diagnostics,
      },
      {
        code: "36224",
        tooltip:
          "CPT 36224 - Selective catheter placement, arterial system; initial fourth order or more selective abdominal, pelvic, or lower extremity artery branch, within a vascular family",
        section: CptCodeSection.Diagnostics,
      },
      {
        code: "36225",
        tooltip:
          "CPT 36225 - Selective catheter placement, arterial system; initial third order or more selective renal or brachial artery branch, within a vascular family",
        section: CptCodeSection.Diagnostics,
      },
      {
        code: "36226",
        tooltip:
          "CPT 36226 - Selective catheter placement, arterial system; initial fourth order or more selective renal or brachial artery branch, within a vascular family",
        section: CptCodeSection.Diagnostics,
      },
      {
        code: "36227",
        tooltip:
          "CPT 36227 - Selective catheter placement, arterial system; initial third order or more selective head or neck artery branch, within a vascular family",
        section: CptCodeSection.Diagnostics,
      },
    ];
  }

  private static getArteryCptCodes(): cptCodesForPriorAuth[] {
    return [
      {
        code: "36140",
        tooltip: "",
        section: CptCodeSection.Diagnostics,
      },
      {
        code: "36200",
        tooltip: "",
        section: CptCodeSection.Diagnostics,
      },
      {
        code: "36245",
        tooltip: "",
        section: CptCodeSection.Diagnostics,
      },
      {
        code: "36246",
        tooltip: "",
        section: CptCodeSection.Diagnostics,
      },
      {
        code: "36247",
        tooltip: "",
        section: CptCodeSection.Diagnostics,
      },
      {
        code: "37252",
        tooltip: "",
        section: CptCodeSection.Diagnostics,
      },
      {
        code: "37253",
        tooltip: "",
        section: CptCodeSection.Diagnostics,
      },
      {
        code: "75610",
        tooltip: "",
        section: CptCodeSection.Diagnostics,
      },
      {
        code: "75625",
        tooltip: "",
        section: CptCodeSection.Diagnostics,
      },
      {
        code: "75630",
        tooltip: "",
        section: CptCodeSection.Diagnostics,
      },
      {
        code: "37220",
        tooltip: "",
        section: CptCodeSection.Interventions,
      },
      {
        code: "37221",
        tooltip: "",
        section: CptCodeSection.Interventions,
      },
      {
        code: "37222",
        tooltip: "",
        section: CptCodeSection.Interventions,
      },
      {
        code: "37223",
        tooltip: "",
        section: CptCodeSection.Interventions,
      },
      {
        code: "37224",
        tooltip: "",
        section: CptCodeSection.Interventions,
      },
      {
        code: "37225",
        tooltip: "",
        section: CptCodeSection.Interventions,
      },
      {
        code: "37226",
        tooltip: "",
        section: CptCodeSection.Interventions,
      },
      {
        code: "37227",
        tooltip: "",
        section: CptCodeSection.Interventions,
      },
      {
        code: "37228",
        tooltip: "",
        section: CptCodeSection.Interventions,
      },
      {
        code: "37229",
        tooltip: "",
        section: CptCodeSection.Interventions,
      },
      {
        code: "37230",
        tooltip: "",
        section: CptCodeSection.Interventions,
      },
      {
        code: "37231",
        tooltip: "",
        section: CptCodeSection.Interventions,
      },
    ];
  }
  private static getVeinCptCodes(): cptCodesForPriorAuth[] {
    return [
      {
        code: "36010",
        tooltip: "",
        section: CptCodeSection.Diagnostics,
      },
      {
        code: "36011",
        tooltip: "",
        section: CptCodeSection.Diagnostics,
      },
      {
        code: "36012",
        tooltip: "",
        section: CptCodeSection.Diagnostics,
      },
      {
        code: "37252",
        tooltip: "",
        section: CptCodeSection.Diagnostics,
      },
      {
        code: "37253",
        tooltip: "",
        section: CptCodeSection.Diagnostics,
      },
      {
        code: "75820",
        tooltip: "",
        section: CptCodeSection.Diagnostics,
      },
      {
        code: "75822",
        tooltip: "",
        section: CptCodeSection.Diagnostics,
      },
      {
        code: "75825",
        tooltip: "",
        section: CptCodeSection.Diagnostics,
      },
      {
        code: "37238",
        tooltip: "",
        section: CptCodeSection.Interventions,
      },
      {
        code: "37239",
        tooltip: "",
        section: CptCodeSection.Interventions,
      },
      {
        code: "37248",
        tooltip: "",
        section: CptCodeSection.Interventions,
      },
      {
        code: "37249",
        tooltip: "",
        section: CptCodeSection.Interventions,
      },
    ];
  }

  static getLabQuestions(): LabQuestion[] {
    return [
      {
        id: "potassium",
        question: "Potassium is less then 5?",
      },
      {
        id: "hemoglobin",
        question: "Hemoglobin is greater then 8?",
      },
      {
        id: "creatinine",
        question: "Is Creatinine less then 1.5?",
      },
      {
        id: "coumadin",
        question: "This patient is currently taking Coumadin?",
      },
      {
        id: "inr",
        question: "Confirm INR is less then 1.7?",
        dependentOn: "coumadin",
      },
    ];
  }

  static getTimeSlotList(
    intervalInMins: number,
    minsAsValue: boolean = true
  ): EnumerationItem[] {
    const startMinutes = 300; // Start time in minutes (5:00 AM)
    const endMinutes = 1380; // End time in minutes (11:00 PM)
    const timeSlots: EnumerationItem[] = [];

    for (
      let minutes = startMinutes;
      minutes <= endMinutes;
      minutes += intervalInMins
    ) {
      const hours = Math.floor(minutes / 60);
      const mins = minutes % 60;
      const period = hours >= 12 ? "PM" : "AM";
      const displayHours = hours > 12 ? hours - 12 : hours === 0 ? 12 : hours;
      const displayString = `${displayHours}:${mins
        .toString()
        .padStart(2, "0")} ${period}`;
      timeSlots.push({
        value: minsAsValue ? minutes.toString() : displayString,
        displayName: displayString,
      });
    }

    return timeSlots;
  }

  static getActivityAbleToMove(): EnumerationItem[] {
    return [
      { value: "2", displayName: "4 extremities" },
      { value: "1", displayName: "2 extremities" },
      { value: "0", displayName: "0 extremities" },
    ];
  }

  static getRespiration(): EnumerationItem[] {
    return [
      { value: "2", displayName: "Able to breathe and cough freely" },
      { value: "1", displayName: "Dyspnea, shallow or limited breathing" },
      { value: "0", displayName: "Apneic" },
    ];
  }

  static getCirculation(): EnumerationItem[] {
    return [
      { value: "2", displayName: "BP ± 20 mmHg of pre-sedation level" },
      { value: "1", displayName: "BP ± 20 -50 mmHg of pre-sedation level" },
      { value: "0", displayName: "BP ± 50 of pre-sedation level" },
    ];
  }

  static getConsciousness(): EnumerationItem[] {
    return [
      { value: "2", displayName: "Fully Awake" },
      { value: "1", displayName: "Arousable on calling" },
      { value: "0", displayName: "Not responding" },
    ];
  }

  static getO2Situration(): EnumerationItem[] {
    return [
      {
        value: "2",
        displayName: "Able to maintain O2 saturation & 92% on room air",
      },
      { value: "1", displayName: "Needs O2 to maintain Saturation & 90%" },
      { value: "0", displayName: "O2 Saturation" },
    ];
  }

  static getNVAPulseLocation(): EnumerationItem[] {
    return [
      { value: "Brachial", displayName: "Brachial" },
      { value: "Femoral", displayName: "Femoral" },
      { value: "Popliteal", displayName: "Popliteal" },
      { value: "Radial", displayName: "Radial" },
      { value: "Dorsalis Pedis", displayName: "Dorsalis Pedis" },
      { value: "Posterior Tibial", displayName: "Posterior Tibial" },
    ];
  }

  static getNVAPulseTypes(): EnumerationItem[] {
    return [
      { value: "Absent", displayName: "Absent" },
      { value: "Faint", displayName: "Faint" },
      { value: "+1", displayName: "+1" },
      { value: "+2", displayName: "+2" },
      { value: "+3", displayName: "+3" },
      { value: "+4", displayName: "+4" },
    ];
  }

  static getPainLevels(): EnumerationItem[] {
    return [
      { value: "1", displayName: "1" },
      { value: "2", displayName: "2 " },
      { value: "3", displayName: "3 " },
      { value: "4", displayName: "4 " },
      { value: "5", displayName: "5 " },
      { value: "6", displayName: "6 " },
      { value: "7", displayName: "7 " },
      { value: "8", displayName: "8 " },
      { value: "9", displayName: "9 " },
      { value: "10", displayName: "10" },
    ];
  }

  static getQuickProcedureTypes(): EnumerationItem[] {
    return [
      { value: "Arterial", displayName: "Arterial" },
      { value: "Venous", displayName: "Venous" },
      { value: "AV Fistula", displayName: "AV Fistula" },
      { value: "Cardiac", displayName: "Cardiac" },
    ];
  }

  static getCalendarEvents(schedules: EncounterSchedule[]): EventInput[] {
    return schedules.flatMap((schedule) => {
      // Skip schedules without checkin time
      if (!schedule.checkIn || schedule.checkIn.trim() === '') {
        return [];
      }

      // Check-In Event
      const checkInEvent = this.createEventDateRange(
        schedule.scheduleDate,
        schedule.checkIn,
        Number(schedule.preProcedureMin) || 0
      );

      // Procedure Event
      const procedureEvent = this.createEventDateRange(
        schedule.scheduleDate,
        schedule.procedureTime,
        Number(schedule.procedureMin) || 0
      );

      // Check-Out Event
      const checkOutEvent = this.createEventDateRange(
        schedule.scheduleDate,
        schedule.checkOut,
        Number(schedule.checkOutMin) || 0
      );

      return [
        {
          start: checkInEvent.start.toISOString(),
          end: checkInEvent.end.toISOString(),
          title: "",
          resourceId: schedule.bedNo || null,
          type: ScheduledEventType.CheckIn,
          scheduleId: schedule.id,
          bedNo: schedule?.bedNo || null,
          hasResource: !!schedule?.bedNo,
          eventLabel: schedule?.patientName,
          formattedTime: DateTime.fromJSDate(checkInEvent.start).toFormat(
            "hh:mm"
          ),
        },
        {
          start: procedureEvent.start.toISOString(),
          end: procedureEvent.end.toISOString(),
          title: schedule.patientName || "",
          resourceId: schedule?.bedNo || null,
          type: ScheduledEventType.Procedure,
          scheduleId: schedule.id,
          bedNo: schedule?.bedNo,
          hasResource: !!schedule?.bedNo,
          eventLabel: schedule?.patientName,
          formattedTime: DateTime.fromJSDate(checkInEvent.start).toFormat(
            "hh:mm"
          ),
        },
        {
          start: checkOutEvent.start.toISOString(),
          end: checkOutEvent.end.toISOString(),
          title: "",
          resourceId: schedule?.bedNo || null,
          type: ScheduledEventType.CheckOut,
          scheduleId: schedule.id,
          bedNo: schedule?.bedNo,
          hasResource: !!schedule?.bedNo,
          eventLabel: schedule?.patientName,
          formattedTime: DateTime.fromJSDate(checkInEvent.start).toFormat(
            "hh:mm"
          ),
        },
      ];
    });
  }

  static createEventDateRange(
    date: string,
    time: string,
    durationMinutes: number
  ): { start: Date; end: Date } {
    const { hour, minute } = this.getHourMins(time);
    const startDate = DateTime.fromFormat(date, "yyyy-MM-dd").set({
      hour,
      minute,
      second: 0,
      millisecond: 0,
    });
    const endDate = startDate.plus({ minutes: durationMinutes || 0 });

    return {
      start: startDate.toJSDate(),
      end: endDate.toJSDate(),
    };
  }

  static addMinsToTimeString(time: string, minutesToAdd: number): string {
    if (time == null || typeof time !== 'string' || !time.trim()) {
      return '';
    }
    const parsedTime = DateTime.fromFormat(time.trim(), "h:mm a");
    if (!parsedTime.isValid) {
      return '';
    }
    const updatedTime = parsedTime.plus({ minutes: minutesToAdd });
    return updatedTime.toFormat("h:mm a");
  }

  static getHourMins(timeString: string): {
    hour: number;
    minute: number;
  } {
    // Split the time into hours, minutes, and period (AM/PM)
    const [time, period] = timeString.split(" ");
    const [checkInHour, checkInMinute] = time.split(":");
    // Convert hours to 24-hour format if needed
    let hoursIn24Format = parseInt(checkInHour, 10);
    if (period === "PM" && hoursIn24Format !== 12) {
      hoursIn24Format += 12;
    } else if (period === "AM" && hoursIn24Format === 12) {
      hoursIn24Format = 0;
    }
    return { hour: hoursIn24Format, minute: Number(checkInMinute) };
  }

  /**
   * Build an ISO-style datetime string from a date string (YYYY-MM-DD) and a time string (e.g. "9:15 AM").
   * Returns the time as selected (e.g. 9:15 AM → 09:15) in YYYY-MM-DDTHH:mm:ss.sss format,
   * without converting to UTC, so the slot time is preserved for the API.
   */
  static buildScheduleDateTime(dateStr: string, timeStr: string): string {
    if (!dateStr?.trim() || !timeStr?.trim()) return '';
    const dateOnly = dateStr.split('T')[0];
    const [y, m, d] = dateOnly.split('-').map(Number);
    if ([y, m, d].some(n => isNaN(n))) return '';
    const { hour, minute } = Utils.getHourMins(timeStr.trim());
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${y}-${pad(m)}-${pad(d)}T${pad(hour)}:${pad(minute)}:00.000`;
  }

  /**
   * Current instant as ISO 8601 / RFC 3339 in **UTC** (always ends with `Z`).
   * Use for metadata fields that must record send time in UTC (e.g. Engage `bookedDateTime`).
   */
  static utcNowIsoString(): string {
    const iso = DateTime.utc().toISO();
    return iso ?? new Date().toISOString();
  }

  static getFollowUpSummaryPrompt() {
    return `You are a physician. Your task is to develop the chief complaint narrative for a follow-up visit of a {age}-year-old {gender} patient who was previously treated for {treatment-for}, and is returning for evaluation of {chief-complaint}.

    Write a brief 2–3 sentence summary describing the patient’s current status, including:
    - Any improvement, worsening, or recurrence of symptoms since the last visit.
    - Ongoing concerns, new symptoms, or changes in response to treatment.

    Use clinical, third-person language (e.g., “The patient reports ongoing improvement in...” or “The patient continues to experience...”).  
    Do not include any headings such as “Chief Complaint:” or “Follow-Up Assessment:”.
    `;
  }

  static getPromptByType(type: PromptType): string {
    switch (type) {
      case PromptType.ValidateFile:
        return `Please summarize the document without including the patient's name, age, or gender. Provide only the results and findings, rephrased in your own words. Avoid using labels such as "Results:" and exclude phrases like "Not Interpreted" or "Not Examined.".
          Return the summary inside a JSON object along with the patient's name, date of birth (DOB), and date of service (DOS) extracted from the document.
          Ensure that:
          - The summary text is concise and well-structured.
          - The DOB and DOS formats remain exactly as they appear in the document (e.g., "MM-DD-YYYY").
          - The date format should **not** be converted into a different format (e.g., avoid "Dec 4, 2024").
          Example JSON output:
          {
            "Summary": "Rephrased summary of the patient's medical details.",
            "PatientName": "John Doe",
            "DOB": "MM-DD-YYYY",
            "DOS": "MM-DD-YYYY"
          }
          Ensure the output is strictly in JSON format without any HTML, extra spaces, slashes, or special characters.`;
      case PromptType.LastEncounter:
        return `Please mark "true" for any conditions listed under "Yes" and provide relevant details in the description if any associated illness is mentioned for the patient in the document. For example:
        {
          "chief_complaint": {
              "Yes": "",
              "description": ""
          },
          "Chest pain or tightness when performing tasks such as mowing, raking leaves, doing laundry, walking up a flight of stairs": {
              "Yes": "",
              "description": ""
          },
          "Pulmonary - COPD, asthma, home oxygen use": {
              "Yes": "",
              "description": ""
          },
          "Kidney- renal insufficiency (CRI), End Stage Renal Disease (ESRD), renal stones, bladder infections": {
              "Yes": "",
              "description": ""
          },
          "Liver - hepatitis, jaundice": {
              "Yes": "",
              "description": ""
          },
          "Endocrine - diabetes, thyroid disease": {
              "Yes": "",
              "description": ""
          },
          "High blood pressure": {
              "Yes": "",
              "description": ""
          },
          "Neurology- fainting spells, seizures, paralysis, stroke or TIA": {
              "Yes": "",
              "description": ""
          },
          "Musculoskeletal- back/neck pain, osteoarthritis, rheumatoid arthritis, muscle disease": {
              "Yes": "",
              "description": ""
          },
          "Gastrointestinal - ulcers, gastritis, acid reflux, hiatal hernia, obesity": {
              "Yes": "",
              "description": ""
          },
          "Blood disorders or cancer- anemia, leukemia, lymphoma, excessive bleeding": {
              "Yes": "",
              "description": ""
          }
        }
        Please ensure no HTML or extra characters are included, and return only the clean JSON provided in the same format.`;
      case PromptType.NonInvasive:
        return `Summarize the document without showing the patient's name, age, and gender. Return only the results and findings. Exclude labels like "Results:" and rephrase in your own words. Exclude words like "Not Interpreted" or "Not examined."`;
      case PromptType.Ultrasound:
        return `Please summarize the document without including the patient's name, age, or gender. Provide only the results and findings, rephrased in your own words. Avoid using labels such as "Results:" and exclude phrases like "Not Interpreted" or "Not Examined."`;
      case PromptType.HPI:
        return `Generate a text summary of "History of Present Illness" section in the document, ignore the rest. Provide a concise overview of the content without including personal information.`;
      case PromptType.FullMetabolic:
        return `Extract lab results from this report. Return JSON only (no markdown).
Numeric keys (use null if missing): coumadin, inr, hemoglobin, potassium, creatinine, calcium, anGap, bun, carbonDioxide, hgb, hct, sodium, glucose, chloride, eGFR, maxContrast.
Also include "analyteResultFlags": an object whose keys are analyte names from the list above when the report shows a flag/interpretation (H, L, High, Low, Critical, Normal, Abnormal, etc.). Values must be exactly one of: "", "Abnormal", "Critical high", "Critical low", "High", "Low", "Negative", "Normal", "Positive", "Stable". Use "" when no flag is printed for that analyte.`;
      case PromptType.MedicationsAndAllergies:
        return `Extract a categorized list of all medications the patient is currently taking in the specified JSON format. Additionally, provide a list of the patient's allergies as comma-separated values and make sure not to include words like "None", "None mentioned". Ensure that the output is strictly in JSON format without any HTML, extra spaces, slashes, or special characters.
            Medications should be categorized under the following groups only if they need to be held before the procedure, according to the latest ACC guidelines (https://www.acc.org/Guidelines). Please ensurse if you are using any medication as a standard to hold and are risks associated like high bleeding or any other higher risks that a procedure can be aborted or it may impact the patients health. Otherwise, categorize them under Other_Medications. Ensure to review the patient’s medical history from the given document and use this information to determine which medications need to be held and which do not, provide a strong reason for each. Please stick to the standard answers.
            Medications_to_hold
            Other_Medications (for any medications not fitting the above category)
            For each medication, include the following details:
            Frequency (e.g., "once a day")
            Quantity/Dosage (e.g., "25 mg" and make sure not to include dosage or quantity like "as needed" or "not spesified", if dosage is not there return empty (i.e ""))
            Biological Half-Life (converted into a proper format such as minutes, hours, or days)
            Ensure the biological half-life is never null. If it is missing, estimate it based on known medical data and convert it into a standardized format (e.g., "20 minutes", "5 hours", "2 days").
            Possible reasons to hold or Possible reasons not to hold depending on the category (e.g., "May cause High Bleeding, Hold 5 days prior to the procedure", "Typically does not need to be held before procedures").
            Allergies should be presented as a simple, comma-separated string.

            {
              "Medications_to_hold": {
                "MEDICATION_NAME": {
                  "frequency": "once a day",
                  "qty or dosage": "25 mg",
                  "biological half-life": "20 hours",
                  "Possible reasons to hold": "May cause hypotension"
                }
              },
              "Other_Medications": {
                "MEDICATION_NAME": {
                  "frequency": "once a day",
                  "qty or dosage": "40 mg",
                  "biological half-life": "24 hours",
                  "Possible reasons not to hold": "Typically does not need to be held before procedures"
                }
              },
              "Allergies": "Peanuts, shellfish"
            }
            The goal is to compile a precise list of medications and inform the patient about which medications need to be stopped before the procedure.
            Ensure the output is strictly in JSON format without any HTML, extra spaces, slashes, or special characters.
            Make sure words like "json\n" etc are not included.`;
      case PromptType.ChiefComplaint:
        return `You are a physician. Your task is to develop the initial subjective assessment for a {age}-year-old {gender} patient presenting with the chief complaint of {chief-complaint}, who is also being treated for {treatment-for}.

        Write a brief 2–3 sentence description summarizing the nature of the chief complaint in clinical, third-person language (e.g., “The patient reports…” or “The patient has been experiencing…”).

        Do not include any headings such as “Chief Complaint:” or “Assessment:”.`;

      case PromptType.PainCharacterizationQuestions:
        return `You are a physician. Your task is to develop the initial subjective assessment for a {age}-year-old {gender} patient presenting with the chief complaint of {chief-complaint}, who is also being treated for {treatment-for}.
            Collect relevant clinical history to clarify the nature, onset, progression, and contributing factors of the chief complaint. Your questions should explore how the issue began, how it has evolved, and what influences it (e.g., position, time of day, activity).
            Only generate questions—do not provide any answers.
            All questions must be medically relevant and clearly related to the chief complaint, the circumstances in which it occurred, and any contextual clinical history that could impact diagnosis or care.
            Categories and Guidance
            Chief_Complaint_Context: Ask about the situation surrounding the onset of the chief complaint, including what the patient was doing, external events (e.g., trauma, activity), and relevant details related to their treatment.

            Onset: Ask when the patient first noticed the issue and whether it began suddenly or gradually.

            Duration: Ask how long the patient has been experiencing the complaint and whether the timing has changed.

            Intensity: Ask about the severity of the complaint and how it has fluctuated over time.

            Triggers: Ask what conditions, movements, or circumstances make it better or worse—especially related to their treatment or lifestyle.

            Return a JSON array containing up to 3 questions per category, formatted like this:
            [
              { "question": "When did you first notice the pain?", "category": "Onset" },
              { "question": "Was the pain sudden or did it build gradually?", "category": "Onset" },
              { "question": "What were you doing at the time the symptoms began?", "category": "Chief_Complaint_Context" },
            ]`;
      case PromptType.HistoryQuestions:
        return `You are a physician. Your task is to develop the initial subjective assessment for a {age}-year-old {gender} patient presenting with the chief complaint of {chief-complaint}, who is also being treated for {treatment-for}.
            Only generate questions—do not provide any answers.
            All questions must be medically relevant and clearly related to the chief complaint
            Categories and Guidance
          ${HpiQuestionType.PastMedicalHistory.replace(
          "_",
          " "
        )}: Ask general questions about previous illnesses, surgeries, or relevant medical events. Do not reference the chief complaint or its treatment.
          ${HpiQuestionType.FamilyHistory.replace(
          "_",
          " "
        )}: Ask general questions about hereditary diseases or chronic conditions in the patient’s immediate family. Do not mention the current complaint or accident.
          ${HpiQuestionType.SocialHistory.replace(
          "_",
          " "
        )}: Ask general questions about lifestyle, habits (e.g., smoking, alcohol), occupation, physical activity, and sleep. Do not tie questions to the specific complaint or its context.
          your task is to generate a JSON similar to the one below, with the max of 3 questions provided under their respective categories:
          [
            { "question": "...", category: "${
          HpiQuestionType.PastMedicalHistory
        }" },
            { "question": "...", category: "${HpiQuestionType.FamilyHistory}" }
          ]`;

      case PromptType.ROS1Questions:
        return `Develop a subjective assessment for a {age}-year-old {gender} patient presenting with the chief complaint of {chief-complaint} and {treatment-for}.
        Focus on collecting relevant clinical history that helps clarify the nature, context, and contributing factors of the complaint.
        Do not provide answers—only generate questions.
        Your output should include targeted and concise questions
          categories:
          General/Constitutional
          Musculoskeletal
          Neurological
          Cardiovascular
          Respiratory
          Gastrointestinal
          All questions must be generic and should not reference the chief complaint or treatment. These are routine screening questions appropriate for a general clinical history.
       your task is to generate a JSON similar to the one below, with the max of 2 questions provided under their respective categories:
        [
          { "question": "...", category: "General/Constitutional" },
          { "question": "...", category: "Musculoskeletal" }
        ]`;
      case PromptType.ROS2Questions:
        return `Develop a subjective assessment for a {age}-year-old {gender} patient presenting with the chief complaint of {chief-complaint} and {treatment-for}.
          Focus on collecting relevant clinical history that helps clarify the nature, context, and contributing factors of the complaint.
          Do not provide answers—only generate questions.
          Your output should include targeted and concise questions
          categories:
          Genitourinary
          Integumentary (Skin)
          Endocrine
          Psychiatric
          Hematologic/Lymphatic
          Allergic/Immunologic
           All questions must be generic and should not reference the chief complaint or treatment. These are routine screening questions appropriate for a general clinical history.
         your task is to generate a JSON similar to the one below, with the max of 2 questions provided under their respective categories:
          [
            { "question": "...", category: "Genitourinary" },
            { "question": "...", category: "Genitourinary" }
          ]`;
      case PromptType.HpiSummary:
        return `Develop the History of Present Illness for a {age}-year-old {gender} patient presenting with the chief complaint of {chief-complaint} and {treatment-for}.
        Focus on collecting relevant clinical history that helps clarify the nature, context, and contributing factors of the complaint.
        ---
        Based on the patient responses provided below, generate a structured clinical summary in valid, clean HTML format. The summary should include the following sections:

        1. History of Present Illness (HPI): Write a well-structured paragraph of approximately 5-6 sentences using only the provided information given below.
        Use a professional and clinical tone. Do not include any heading inside the paragraph,
        but wrap it in a <p> tag with class "hpi-summary".

        2. Past Medical History: If relevant past medical information is provided below, include h5 tag “Past Medical History:” followed by a bulleted list using <ul> and <li> tags.
        3. Family History: If family history information is provided below, include h5 tag “Family History:” followed by a bulleted list.
        4. Social History: If social history details are provided below, include h5 tag “Social History:” followed by a bulleted list.
        Use only the data provided. Do not add, infer, or assume any additional information. Maintain medical accuracy and return valid HTML only.

        Patient response sources:
        - patient responses and answers = {ques-answer}  \n
        - Medical History = {med-his}    \n
        - Family History = {fam-his}    \n
        - Social History = {soc-his}`;
      case PromptType.RosSummary:
        // return `Develop the Review of Systems for a {age}-year-old {gender} patient presenting with the chief complaint of {chief-complaint}.
        // Focus on collecting relevant clinical history that helps clarify the nature, context, and contributing factors of the complaint.
        // ---
        // Based on the patient responses provided below, generate a structured clinical summary in valid, clean HTML format. The summary should include the following sections:

        // 1. Review of Systems (ROS): Write a well-structured paragraph of approximately 5-6 sentences using only the provided information given below.
        // Use a professional and clinical tone. Do not include any heading inside the paragraph,
        // but wrap it in a <p> tag with class "ros-summary".

        // Use only the data provided. Do not add, infer, or assume any additional information. Maintain medical accuracy and return valid HTML only.

        // Patient's subjective assessment:
        // - patient responses and answers are as follows:

        // {ques-answer}

        // Include following part as it is at the end of the summary.
        // {ques-answer2}`;
        return `Develop the Review of Systems for a {age}-year-old {gender} patient presenting with the chief complaint of {chief-complaint}.

        Based on the patient responses provided below, generate a structured clinical summary in valid, clean HTML format.

        Review of Systems (ROS):

        - Format the content strictly according to the provided Output format instructions.
        - Do NOT add any headings unless explicitly requested in Output format.

        - Convert the patient responses into concise clinical statements grouped logically by body system when possible.

        - The ROS should contain multiple meaningful clinical statements, not a single sentence summary.

        - If a system is mentioned but details are missing, state it as unspecified rather than omitting it.
          Example: "Gastrointestinal symptoms not specified".

        - You may rephrase patient wording into professional clinical language.
        - Do NOT invent symptoms, negatives, durations, or medical history not present in the responses.

        - The length should adapt to the available information:
          • minimal responses → short ROS
          • detailed responses → longer ROS

        - Never leave the ROS empty.

        Return valid HTML only.

        Patient's subjective assessment (input only — do not copy question stems into the output):
        - patient responses and answers are as follows:

        {ques-answer}

        Output rules:
        - Write each body system once in clinical language (e.g., "Denies fever", "Reports knee pain").
        - Do NOT append a second pass, duplicate system headings, or raw questionnaire lines.
        - Do NOT output "Positive for Have you…?" or "Negative for Have you…?" phrasing.
        - Do NOT include placeholder tokens such as {ques-answer2}.`;
      default:
        return "";
    }
  }

  /**
   * Returns the prompt template used to generate History of Present Illness summary.
   * Placeholders: {age}, {gender}, {chief-complaint}, {ques-answer}.
   * Used by HpiQuestionsService and for display in HPI triage footer.
   */
  static getHpiSummaryGenerationPromptTemplate(): string {
    //     return `Develop the History of Present Illness for a {age}-year-old {gender} patient presenting with the chief complaint of {chief-complaint}.
    // Focus on collecting relevant clinical history that helps clarify the nature, context, and contributing factors of the complaint.
    // ---
    // Based on the patient responses provided below, generate two outputs:

    // 1. 'summaryHtml' — structured clinical summary in valid, clean HTML format. It should include:
    //   - 'History of Present Illness (HPI):'
    //     - Write a well-structured paragraph of approximately 5–6 sentences using only the provided information given below.
    //     - Use a professional, clinical tone.
    //     - Wrap the paragraph inside a <p> tag with class 'hpi-summary'.
    //     - Do not add, infer, or assume any extra information.
    //   - 'Associated Symptoms (optional):'
    //     - After the HPI paragraph, if and only if the 'Associated Symptoms' question has a non-empty answer,
    //       include a small heading <b style="font-size: 14px;">Associated Symptoms</b> followed by a <ul> list with class 'associated-symptoms'.
    //     - Each bullet point (<li>) should use the official **SNOMED CT preferred term** of the symptom (not the informal text from the patient response).
    //       Example: if the symptom is "SOB with activity", use "Dyspnea on exertion".
    //     - Do not infer or add symptoms not mentioned in that answer.

    // 2. 'associatedSymptomsJson' — a structured JSON array.
    //   - Extract symptoms only from the 'Associated Symptoms' answer.
    //   - For each symptom, identify the corresponding **SNOMED CT preferred term** and include its SNOMED Concept ID if known.
    //   - Each entry in the array must have the following structure:
    //     {
    //       'symptom': 'Dyspnea on exertion',  // SNOMED Preferred Term
    //       'snomedCode': '60845006',          // SNOMED Concept ID
    //       'confidenceScore': '95%',          // confidence in % (e.g., 90%)
    //       'description': 'Shortness of breath occurring during physical activity or exertion.',
    //       'isPainRelated': false
    //     }
    //   - Use SNOMED CT preferred terms only (not raw patient phrasing).
    //   - If no exact match is found, provide the closest clinically relevant SNOMED term and set confidence accordingly.
    //   - If the answer is missing or empty, return an empty array [].

    // Return the final output in this JSON format:
    // {
    //   'summaryHtml': '<p class="hpi-summary"> ... </p><b style="font-size: 14px;">Associated Symptoms</b><ul class="associated-symptoms">...</ul>',
    //   'associatedSymptomsJson': [ ... ]
    // }

    // Patient's subjective assessment:
    // - patient responses and answers = {ques-answer}
    // `;

    return `Develop the History of Present Illness for a {age}-year-old {gender} patient presenting with the chief complaint of {chief-complaint}.

Based on the patient responses provided below, generate two outputs:

1. 'summaryHtml' — structured clinical summary in valid, clean HTML format.

  - Format the content strictly according to the provided Output format instructions.
  - Do NOT add any titles or headings unless explicitly requested in Output format.

  - The HPI must contain multiple clinically meaningful statements, not a single sentence summary.

  - Expand the chief complaint into standard clinical attributes when details are missing.
    Include commonly expected clinical dimensions such as:
      location, duration, severity, quality, timing, modifying factors, and associated symptoms.

  - If a detail is not provided by the patient, explicitly state it as unspecified rather than omitting it.
    Example: "Duration not specified", "Severity not reported".

  - You may rephrase patient wording into professional clinical language.
  - Do NOT invent specific values (no fake durations, measurements, diagnoses, or history).

  - The goal is to create a clinically useful HPI rather than restating the complaint.

  - Never produce fewer than 4 clinical statements unless the Output format restricts it.
  - Never leave the HPI empty.

  - 'Associated Symptoms (optional):'
    - After the HPI content, if and only if the 'Associated Symptoms' question has a non-empty answer,
      include:
        <b style="font-size: 14px;">Associated Symptoms</b>
      followed by an HTML list.

    - Each list item must use the official SNOMED CT preferred term (not patient wording).
      Example: "SOB with activity" → "Dyspnea on exertion"
    - Do not infer or add symptoms not explicitly mentioned.

2. 'associatedSymptomsJson' — a structured JSON array.

  Extract symptoms only from the 'Associated Symptoms' answer.

  Each entry must follow:
  {
    'symptom': 'Dyspnea on exertion',
    'snomedCode': '60845006',
    'confidenceScore': '95%',
    'description': 'Shortness of breath occurring during physical activity or exertion.',
    'isPainRelated': false
  }

  - Use SNOMED CT preferred terms only.
  - If closest match used → lower confidence.
  - If empty → return [].

Return the final output in JSON format:

{
  'summaryHtml': '<formatted according to Output format>',
  'associatedSymptomsJson': [ ... ]
}

Patient's subjective assessment:
- patient responses and answers = {ques-answer}

Output format:
{output-format}
`;
  }

  /**
   * Returns the prompt template used to generate Review of Systems summary.
   * Placeholders: {age}, {gender}, {chief-complaint}, {ques-answer}, {ques-answer2}.
   * Used by RosQuestionsService and for display in ROS triage footer.
   */
  static getRosSummaryGenerationPromptTemplate(): string {
    return Utils.getPromptByType(PromptType.RosSummary);
  }

  /**
   * Returns the prompt template used to generate Patient History summary.
   * Placeholder: {INPUT_DATA}. Used by HpiQuestionsService and for display in History triage footer.
   */
  static getHistorySummaryGenerationPromptTemplate(): string {
    return Utils.getGenerateHistoryContextSummaryPrompt(
      { title: 'History', templateEntity: { templateSubTypes: ['Subjective'] } },
      []
    );
  }

  static fixBrokenJsonArray(jsonString: string): string {
    // Trim leading/trailing whitespace
    jsonString = jsonString.trim();

    // Add opening square bracket if missing
    if (!jsonString.startsWith("[")) {
      jsonString = "[" + jsonString;
    }

    // Add closing square bracket if missing
    if (!jsonString.endsWith("]")) {
      jsonString = jsonString + "]";
    }

    // Fix trailing commas before closing brackets (optional extra fix)
    jsonString = jsonString.replace(/,(\s*[}\]])/g, "$1");

    return jsonString;
  }

  static getPromptForCreatingContextSummaryFromGivenFile(documentType: string = ''): string {
    const prompt = `
    You are a clinical expert. Your task is to review a medical document and provide a concise, meaningful, and professional summary of the key medical findings, impressions, and any relevant labs or orders.
    
    Document type: ` + documentType + `
    
    Guidelines:
    
    - Summarize the document in a single, concise paragraph highlighting only the essential medical findings, impressions, and pertinent lab results or orders.
    - For clinical or diagnostic documents (e.g., X-Ray, Ultrasound, ABI, SOAP notes, Last Encounter), focus on key findings and impressions in professional clinical language.
    - For labs or orders (e.g., blood tests, imaging orders, procedure requests), summarize the relevant results or orders clearly in paragraph form.
    - Use plain text only; avoid headings, bullet points, or any formatting.
    - Exclude patient identifiers, dates, clinician names, and administrative details.
    - Focus strictly on the medical content and relevant findings.
    `;

    return prompt;
  }

  static getPromptForMiscContextSummary(note: string): string {
    const trimmedNote = note?.trim() ?? '';

      return `User: |
      ${trimmedNote}`;
  }

  static getMiscContextFormattingPrompt(note: string, format: string, length: string, context: string[] = []): string {
    const prompt = note?.trim() ?? '';
    const normalizedFormat = format && format.trim() ? format.trim() : 'Auto';
    const normalizedLength = length && length.trim() ? length.trim() : 'Auto';
    const contextInstructions = context.length > 0 ? context.join(', ') : '';
    const formatInstruction = Utils.getMiscFormatInstruction(normalizedFormat);
    const lengthInstruction = Utils.getMiscLengthInstruction(normalizedLength);

    return `User:
        You are a Clinical Documentation Expert with extensive experience in SOAP note documentation, medical coding, and compliance with CMS, MIPS, CPT, AMA and E/M guidelines.

        **CONTENT TO FORMAT:**
        """
        ${prompt}
        """

        **Prompt (User Input):**  
        ${prompt}  # The instruction or request entered by the user, e.g., "Write a Plan"
        
        **Optional Context (If Provided):**  
        ${context}  # Any relevant patient information, such as demographics, medical history, or findings from Subjective, Objective, Assessment, or Plan (S/O/A/P).  
        # Context should be used **only to enhance the clinical relevance, accuracy, and quality** of the AI-generated output.
        # You MUST use the EXACT age and gender if available.
        # Do NOT change, estimate, or make up different demographics
        
        **Formatting Preference:**  
        ${formatInstruction}  # Paragraph, Bulleted, Numbered, Auto
        
        **Length Preference:**  
        ${lengthInstruction}  # Concise, Standard, Detailed, Auto
        
        ---------------------------------------------------------
        **TASK AND EXPECTATIONS**
        ---------------------------------------------------------
        
        1. **User-Request-Centric Output**
          - Generate output **strictly according to the user’s prompt**.  
          - Do not create additional sections, commentary, or content beyond what is explicitly requested.  
          - Ensure the output fully satisfies the **intent, scope, and requirements** of the user input.
        
        2. **Context Integration**
          - Use optional context **to enhance clinical relevance, accuracy, completeness, and clarity**.  
          - Integrate context naturally to improve phrasing, detail, and professional tone.  
          - Do not fabricate unrelated clinical information.  
          - The output should be **high-quality, EMR-ready, and clinically appropriate**.
        
        3. **Compliance with Professional Standards**
          - Ensure all output aligns with **recognized clinical documentation best practices**, including CMS, MIPS, CPT, and E/M guidelines.  
          - Outputs must be **accurate, internally consistent, evidence-based, and clinically relevant**.  
          - Maintain a **professional, EMR-appropriate tone** suitable for medical records and regulatory compliance.  
          - Documentation should be **clear, concise, structured, and suitable for professional medical records**, supporting clinical decision-making and continuity of care.
        
        4. **Formatting and Length**
          - Apply the requested formatting and length while maintaining clarity, readability, and professional presentation.
        
          - **Paragraph:** plain text, coherent professional sentences  
            - Concise: 2–4 lines  
            - Standard: 3–6 lines  
            - Detailed: 6–10 lines  
            - Paragraph output **must not contain any HTML tags**, including <p>.
        
          - **Bulleted List:** Output as valid HTML using <ul> and <li> tags.  
            - Format: <ul><li>Item 1</li><li>Item 2</li><li>Item 3</li></ul>  
            - Concise: 2–3 items, Standard: 4–6 items, Detailed: 6–8 items  
            - Do **NOT** wrap list items in <p> tags or add any extra HTML tags inside <li>.  
            - Do **NOT** use markdown bullet symbols like "-", "*", or "•" — use only <li> tags.  
            - Each <li> should contain only the text content directly, no leading symbols or numbers.
        
          - **Numbered List:** valid HTML <ol><li>…</li></ol>  
            - Each numbered item must appear on its **own line**.  
            - Length guidance same as Bulleted List.  
            - Do **not wrap list items in <p> tags**.  
            - Each <li> should begin directly with the sentence; no extra symbols inside <li>.
        
          - **Auto:** AI selects the most appropriate format and length based on content and context.  
            - If a list is generated in Auto format, it **must use <ul> or <ol>** for multiple items.  
          - Maintain valid HTML for all lists; Paragraph outputs **must remain plain text without HTML**.
        
        5. **Output Integrity**
          - Output must be **clinically accurate, professional, and Medical Billing ready**.  
          - Do not include disclaimers, meta-commentary, or guideline references in the output.  
          - Deliver **only what the user requested**, enriched by context, formatted as specified, and aligned with professional standards.

        6. **NO SECTION HEADINGS - CRITICAL**
          - DO NOT include ANY section headings or labels in the output
          - NEVER use headings like: "Subjective:", "Objective:", "Assessment:", "Plan:", "HPI:", "Subjective HPI:", "Chief Complaint:", "History:", "Physical Exam:", "Diagnosis:", "Treatment:", etc.
          - Write as a CLEAN, CONTINUOUS narrative without any bold section labels
          - Integrate all information naturally into flowing sentences without dividing into labeled sections
          - The output should read as one cohesive summary, not as a structured SOAP note with headers

        7. **Restrictions on Conversational or Non-Clinical Output**
          - Do not produce conversational language, greetings, or intake-style statements.
          - Do not generate questions back to the user.
          - Do not create introductory or transitional phrases not requested by the user.
          - Output must remain strictly clinical, professional, and aligned with the structure and tone of medical documentation.
            `;
          }

  static getMiscFormatInstruction(format: string): string {
    if (!format || typeof format !== 'string') {
      return 'Choose the clearest structure for the information; default to well-structured paragraphs when unsure.';
    }
    const normalized = format.trim();
    const capitalized = normalized.charAt(0).toUpperCase() + normalized.slice(1).toLowerCase();
    switch (capitalized) {
      case 'Paragraph':
        return 'Write cohesive paragraphs with complete sentences and logical flow.';
      case 'Bulleted':
        return 'Present the content as a bulleted list using HTML <ul><li>...</li></ul> tags. Do not use "-", "*", or "•" symbols.';
      case 'Numbered':
        return 'Present the content as a numbered list using HTML <ol><li>...</li></ol> tags. Do not use "1.", "2.", etc. markers.';
      default:
        return 'Choose the clearest structure for the information; default to well-structured paragraphs when unsure.';
    }
  }

  static getMiscLengthInstruction(length: string): string {
    if (!length || typeof length !== 'string') {
      return 'Maintain approximately the same level of detail as the source note, only adjusting wording for clarity.';
    }
    const normalized = length.trim();
    const capitalized = normalized.charAt(0).toUpperCase() + normalized.slice(1).toLowerCase();
    switch (capitalized) {
      case 'Detailed':
        return 'Provide an expanded, thorough version that retains all clinical details and adds clarifying transitions when helpful.';
      case 'Concise':
        return 'Deliver a brief summary that captures only the essential clinical points in as few sentences as possible.';
      case 'Standard':
        return 'Maintain a balanced level of detail similar to a typical clinical note, preserving key findings and rationale.';
      default:
        return 'Maintain approximately the same level of detail as the source note, only adjusting wording for clarity.';
    }
  }

  static getSoapNoteSummary(htmlString: string): string {
    if (!htmlString) {
      return "";
    }
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = htmlString;

    const paragraphs = tempDiv.querySelectorAll("p");

    if (paragraphs.length > 0) {
      return paragraphs[0].textContent.trim();
    }

    return tempDiv.innerText || tempDiv.textContent || "";
  }

  static getAyaPrompt(hpi: string, note: string, request: string): string {
    // return ` HPI: ${hpi}
    //   "Note: ${note}
    //   Perform the following actions on the HPI and note:
    //   1. ${request}
    //   2. return the json object in a format that can be used in a javascript code.
    //   3. never return plain text, always return the json object.
    // `;
    return `
      "Note: ${note}
      Perform the following actions on the HPI and note:
      1. ${request}
      2. Return the response as a valid HTML snippet that can be directly used in a webpage.
      3. Do NOT include <!DOCTYPE>, <html>, <head>, <meta>, <title>, or <body> tags.
      4. The output should contain only the inner HTML content such as <div>, <style>, <h2>, <p>, etc., which can be directly inserted into an existing HTML document or Angular template.
      5. Do NOT return plain text or JSON — always return valid, styled HTML content only.
    `;
  }

  static getAyaTemplatePrompt(note: string, type: string, variables: string, request: string): string {
    return `
      "Note: ${note}
      type: ${type}
      variables: ${variables}
      Perform the following actions on the note:
      1. ${request}
      2. Return the response as a valid HTML snippet that can be directly used in a webpage.
      3. Do NOT include <!DOCTYPE>, <html>, <head>, <meta>, <title>, or <body> tags.
      4. The output should contain only the inner HTML content such as <div>, <style>, <h2>, <p>, etc., which can be directly inserted into an existing HTML document or Angular template.
      5. Do NOT return plain text, JSON, CSS style, or images — always return valid HTML content only without styles or images.
    `;
  }

  static getAyaTemplateHTML(prompt: string): string {
    return `
    Perform the following actions:
    1. Write complete HTML code for the following content: ${prompt}.
    2. Ensure that the HTML content includes all necessary elements and sections.
    3. Include corresponding input fields for each section where answers can be entered.
    4. Do NOT return plain text, JSON, or images — always return valid HTML content.
    `;
  }

  static contextChangeCommandPrompt(text: string, request: string): string {
    return `
      "Note: ${text}

      Perform the following actions on the note:
      1. ${request}

      If you are unable to understand the instruction or the request is unclear, do not ask for clarification—just return the original note exactly as it was provided above without the label "Note:" and without double quotes.
    `;
  }

  static miscContextChangeCommandPrompt(text: string, request: string, contextType: string): string {
    return `
      "You are a clinical documentation assistant specialized in EMR (Electronic Medical Record) data.

        User Input (Note):
        ${text}

        Task:
        Review and modify the note based on the user’s request.

        Instruction:
        ${request}

        Context Type:
        ${contextType}  # describes the type of clinical or contextual data provided

        Guidelines:
        - Only perform actions that relate to clinical documentation, such as editing or generating SOAP notes, histories (social, family, medical), assessments, plans, or other EMR-related text.
        - If the user’s request is not related to EMR or medical documentation (for example, general chat, coding, entertainment, or unrelated questions), ignore the instruction and simply return the original note exactly as provided.
        - Do not ask for clarification or additional information. If any part of the request or context is unclear, contradictory, or incomplete, return the note exactly as it was written (without the label “Note:” and without quotation marks).
        - Maintain a professional, clinical tone suitable for medical records.
        - Do not include any disclaimers, explanations, or additional commentary in the output.

        
    `;
  }

  static createSymptomsPrompt(chiefComplaint: string): string {
    return `
      You are a medical AI assistant. Based on the following chief complaint, generate a comprehensive list of only symptoms based on snomed titles and codes that are present in cheif complaint and should be evaluated during the patient encounter.

      Chief Complaint: "${chiefComplaint}"

      Instructions:
      1. Analyze the chief complaint and identify the primary medical symptom
      2. Generate symptoms that are present in chief complaint
      3. Ensure the symptoms are based on snomed titles and codes
      4. Ensure the symptoms are specific, measurable, and clinically relevant
      5. Use medical terminology appropriate for clinical documentation
      6. Determine whether the symptom itself represents pain, discomfort, tenderness, soreness, or painful sensation in any body part.
      7. Only set "isPainRelated" to true if the symptom directly indicates pain or discomfort (e.g., headache, chest pain, abdominal tenderness, sore throat).
      8. If the symptom does not inherently describe pain (e.g., vomiting, nausea, dizziness, fatigue, cough), set "isPainRelated" to false even if it often co-occurs with pain.
      9. Symptoms name should be the Closest SNOMED CT concept and related code
        e.g.
        Lack of motivation = Avolition 271327008
        Being tired = Fatigue / 84229001
        Anxiety = Anxiety / 43723005
        Depression = Depression / 440540003
        Anxiety = Anxiety / 43723005
      10. Return the symptoms as a JSON array of objects with the following structure:
        [
          {
            "symptom": "Vomiting",
            "snomedCode": "422400008",
            "confidenceScore": "95%",
            "description": "Patient is experiencing expulsion of stomach contents through the mouth.",
            "isPainRelated": "false"
          },
          {
            "symptom": "Nausea",
            "snomedCode": "422587007",
            "confidenceScore": "80%",
            "description": "Nausea often accompanies vomiting and indicates an urge to vomit.",
            "isPainRelated": "false"
          }
        ]

      Return ONLY the JSON array, no additional text or explanation.
    `;
  }

  static createPromptForGettingCustomSymptomDetails(symptomName: string): string {
    return `
      You are a medical AI assistant. Based on the following symptom name provided by the user, return its SNOMED CT code, confidence score, and a brief description.

      Symptom: "${symptomName}"

      Instructions:
      1. Search for the most accurate SNOMED CT concept that matches the provided symptom name
      2. If an exact match is found, return the result in the following JSON format:
          {
            "symptom": "Snomed name",
            "snomedCode": "Snomed code",
            "confidenceScore": "90%",
            "description": "Brief description of the symptom"
          }
      3. If only a close match is found, return it with a lower confidence score (e.g., 60–80%)
      4. If no match exists in the SNOMED CT database, return this JSON response:
        {
          "error": "No SNOMED CT concept found for the given symptom"
        }
      5. Do not include any extra explanation outside of the JSON array.
    `;
  }

  static tagsQuestionsPrompt(tags: string[]): string {
    const prompt = `
      As an AI, your task is to generate subjective assessment questions for each medical symptom tag provided below. Each tag represents a symptom or clinical concept. For every tag, return clinically relevant and medically accurate questions using the PPQRSTA format. Return your output in the following JSON format:

      {
        "TagName": [
          "Question 1",
          "Question 2",
          ...
        ]
      }

      Instructions:

      - Treat each tag as a medical symptom (e.g., “hpi” = History of Present Illness).
      - For each tag, generate up to 7 clinically appropriate questions following the PPQRSTA structure:
        - Provocation/Palliation
        - Position/Place
        - Quality
        - Radiation
        - Severity
        - Timing
        - Associated Symptoms

      - If a tag refers to an auto injury, auto-injury, car accident, or similar trauma:
        - Use "Auto injury" as the key in the JSON.
        - Generate up to 10 detailed questions.
        - Do not use the PPQRSTA format.
        - Focus instead on:
          - Incident date and location
          - Events before, during, and after the incident
          - Patient’s position and role
          - Immediate symptoms and injuries
          - Emergency care and follow-up treatments

      - All questions must:
        - Be phrased in third person (e.g., “Has the patient...”, “Where did the patient…”)
        - Avoid the use of “you”, “your”, or second-person phrasing
        - Be neutral and non-leading
          - ❌ Avoid: “such as”, “for example”, “etc.”
          - ❌ Avoid: “Can the patient describe the pain, such as burning or stabbing?”
          - ✅ Use: “How would the patient describe the pain?”

      - Only include clinically meaningful symptoms as keys — do not invent or use vague/non-medical tags.
        - ✅ Valid example: "Neck pain"
        - ❌ Invalid example: "General symptoms"

      - Your output must:
        - Contain only questions, not answers
        - Return one key per tag
        - Follow strict medical relevance and structure
        - Maintain up to 7 questions per tag (10 for auto injuries)

      Tags: ${tags.join(", ")}
      `;
    return prompt;
  }

  //   static hpiQuestionsPrompt(): string {
  //     return `
  //       You are a physician. Your task is to develop the initial subjective assessment for a {age}-year-old {gender} patient presenting with the chief complaint of {chief-complaint}.

  //       Your objectives:
  //       - Identify all possible medically relevant symptom(s) and/or system(s) related to the chief complaint.
  //       - For each identified symptom/system, generate up to 7 clinically appropriate questions using the PPQRSTA format to clarify the nature and context of the complaint.

  //       Instructions:
  //       - Only generate questions, not answers.
  //       - Return multiple systems whenever appropriate — do not limit output to just one.
  // For example, if the chief complaint is headache, relevant systems may include:
  //         -- Neurological
  //         -- Ophthalmologic (Eye)
  //         -- ENT (Ear, Nose, Throat)
  //         -- Vascular
  //         -- Musculoskeletal
  //         -- Psychiatric
  //         -- Medication or Substance-Related
  //         -- Systemic / Infectious / Other
  //       - All questions must:
  //         -- Be clinically relevant
  //         -- Be focused on evaluating the symptom(s)/system(s)
  //         -- Follow the PPQRSTA structure below
  //         -- Be unique and specific to the system they're under
  //       - Never return vague or non-clinical terms such as:
  //         -- "General"
  //         -- "Non-Specific Complaint"
  //       Instead, use well-defined medical systems or anatomical/clinical terms relevant to the complaint.

  //       PPQRSTA Format Guide:

  //       - Provocation/Palliation: What makes the symptom better or worse? (e.g., specific actions, positions, or treatments)
  //       - Position/Place: Where exactly is the symptom located? Is it localized or diffuse?
  //       - Quality: How does the patient describe the symptom? (e.g., sharp, dull, throbbing)
  //       - Radiation: Does the symptom spread to other areas?
  //       - Severity: How intense is the symptom? Can it be rated or compared to past experiences?
  //       - Timing: When did it start? How often does it occur? How long does it last? Are there any patterns?
  //       - Associated Symptoms: Are there any other symptoms present alongside the chief complaint?

  //       Output Format:

  //       Return a JSON array, with up to 7 questions per system, following the PPQRSTA format:

  //       {
  //         "Neurological": [
  //           "Question 1 related to Provocation/Palliation",
  //           "Question 2 related to Position/Place",
  //           "Question 3 related to Quality",
  //           "Question 4 related to Radiation",
  //           "Question 5 related to Severity",
  //           "Question 6 related to Timing",
  //           "Question 7 related to Associated Symptoms"
  //         ],
  //         "ENT": [
  //           "Question 1 related to Provocation/Palliation",
  //           ...
  //         ],
  //         ...
  //       }
  //     `;
  //   }

  static rosQuestionsPrompt(): string {
    return `
      You are a physician. Your task is to develop a Review of Systems (ROS) questionnaire
      for a {age}-year-old {gender} patient presenting with the chief complaint of {chief-complaint}.

      Previously Identified Symptoms:
      --------------------------------
      {symptoms}

      ===============================================================================
      Objectives:
      ===============================================================================
      1. Generate system-based screening questions across all relevant organ systems:
        - Integumentary System
        - Skeletal System
        - Muscular System
        - Nervous System
        - Endocrine System
        - Cardiovascular (Circulatory) System
        - Lymphatic/Immune System
        - Respiratory System
        - Digestive System
        - Urinary (Excretory) System
        - Reproductive System

      2. Prioritize:
        - Chief complaint-related systems first (detailed).
        - Then add screening questions from other systems (brief, high-yield).

      3. Each system may include 3–7 clinically relevant questions.

      4. Questions must be phrased in third person:
        ✅ "Has the patient experienced..."
        ✅ "Does the patient report..."

      ===============================================================================
      Question Phrasing Rules:
      ===============================================================================
      - Do NOT use "you" or "your".
      - Do NOT suggest answers.
      - Avoid vague or non-clinical terms.
      - Each question should screen for a specific symptom or finding.

      ===============================================================================
      Coding Requirements (ONC / Meaningful Use):
      ===============================================================================
      - Problems/Diagnoses → SNOMED CT, ICD-10-CM
      - Labs → LOINC
      - Medications → RxNorm
      - Social History → SNOMED CT

      ===============================================================================
      JSON Output Format:
      ===============================================================================
      - Each system is a JSON key.
      - Each system maps to an array of questions with metadata.

      Example Structure:
      ------------------
      {
        "System Name": [
          {
            "question": "Has the patient experienced unexpected weight loss?",
            "question_loinc_title": "Weight loss symptom",
            "question_loinc_code": "71483-1",
            "category": "Medical History",
            "code_system": ["SNOMED", "ICD-10", "LOINC"],
            "codes": {
              "SNOMED": "422400008",
              "ICD-10": "R63.4",
              "LOINC": "71483-1"
            }
          }
        ]
      }

      ===============================================================================
      Example Output:
      ===============================================================================
      {
        "Integumentary System": [
          {
            "question": "Has the patient experienced fever or chills?",
            "question_loinc_title": "Fever or chills",
            "question_loinc_code": "75326-9",
            "category": "Medical History",
            "code_system": ["SNOMED", "ICD-10", "LOINC"],
            "codes": {
              "SNOMED": "386661006",
              "ICD-10": "R50.9",
              "LOINC": "75326-9"
            }
          },
          {
            "question": "Has the patient noticed unexpected weight changes?",
            "question_loinc_title": "Weight change",
            "question_loinc_code": "71483-1",
            "category": "Medical History",
            "code_system": ["SNOMED", "ICD-10", "LOINC"],
            "codes": {
              "SNOMED": "422400008",
              "ICD-10": "R63.5",
              "LOINC": "71483-1"
            }
          }
        ]
      }
    `;
  }

  static hpiQuestionsPrompt(): string {
    return `
      You are a physician. Your task is to develop the initial subjective assessment for a {age}-year-old {gender} patient presenting with the chief complaint of {chief-complaint}.

      Previously Identified Symptoms:
      --------------------------------
      {symptoms}

      Objectives:
      1. Focus on the symptoms listed above. For each symptom, generate up to 7 clinically appropriate questions to clarify the nature and context of that symptom.
      2. If no symptoms are listed above, identify relevant symptoms based on the chief complaint and generate questions for each.

      Special Case: Auto Injury
      --------------------------------
      If the symptom is “auto injury” OR “auto-injury” OR “auto accident” OR “car accident”:
      - Return “Auto injury” as the first symptom in the JSON output.
      - Generate up to 10 clinically appropriate questions.
      - Focus on complete injury details:
          • Location and date of the incident
          • Events before, during, and after
          • Patient’s position in the vehicle
          • Injuries sustained
          • Care received
          • Current symptoms

      Question Phrasing Rules:
      --------------------------------
      - All questions must be written in third person, as if asked by a clinician:
          ✅ “Has the patient experienced…”
          ✅ “How did the patient describe…”
          ✅ “Where was the patient located when…”
      - Do NOT use “you” or “your”.
      - Do NOT include examples inside the question text.
      - Do NOT use leading language (avoid suggesting answers).

      Instructions:
      - Only generate questions, not answers.
      - Return multiple symptoms whenever appropriate — do not limit output to one.
      - For example, if the chief complaint is headache, relevant symptoms might include:
        -- Nausea
        -- Dizziness
        -- Visual disturbances
        -- Neck pain
        -- Jaw pain
        -- Scalp tenderness
        -- Light sensitivity
        -- Eye pressure

      - All questions must:
        -- Be clinically relevant
        -- Be focused on evaluating the specific symptom
        -- Be unique and specific to the symptom they relate to

      Symptom Naming Requirements:
      --------------------------------
      - Use only well-defined medical symptoms
      - Do not use vague or non-clinical symptom names, such as:
        --- "General symptoms"
        --- "Non-specific discomfort"
      - Each symptom name must be no more than two words

      Special Instructions for Injuries:
      - If symptoms are related to a general injury, include follow-up questions about:
        -- What occurred before, during, and after the injury
      - If symptoms are related to auto injury, auto-injury, car accident, or auto accident:
        -- Return “Auto injury” as the first key in the JSON
        -- Generate up to 10 detailed questions
        -- Include:
          --- Location and date of the incident
          --- Events before, during, and after
          --- Patient’s position, injuries, care received, and symptoms

      Coding Requirements (ONC / Meaningful Use):
      --------------------------------
      - Problems/Diagnoses → Include SNOMED CT and ICD-10-CM
      - Labs → LOINC
      - Medications → RxNorm
      - Social History items (e.g., smoking, alcohol, substance use) → SNOMED CT
      - Other history (medical, surgical, family) → SNOMED CT

      JSON Output Format:
      --------------------------------
      - The JSON must be valid and well-formed.
      - The JSON object must have one key per symptom name.
      - Each value is an array of questions with metadata fields.

      {
        "Symptom name": [
          {
            "question": "Question text here?",
            "question_loinc_title": "Loinc title",
            "question_loinc_code": "Loinc code",
            "category": "Medical History | Surgical History | Family History | Social History",
            "code_system": ["SNOMED", "ICD-10", "LOINC", "RxNorm"],
            "codes": {
              "SNOMED": "123456",
              "ICD-10": "I10"
            }
          }
        ]
      }

      Example Output:
      {
        "Auto injury": [
          {
            "question": "What was the exact date of the auto injury?",
            "question_loinc_title": "Loinc title",
            "question_loinc_code": "Loinc code",
            "category": "Medical History",
            "code_system": ["SNOMED"],
            "codes": { "SNOMED": "217082002" }
          },
          {
            "question": "Was the patient the driver or passenger during the incident?",
            "question_loinc_title": "Loinc title",
            "question_loinc_code": "Loinc code",
            "category": "Social History",
            "code_system": ["SNOMED"],
            "codes": { "SNOMED": "364703007" }
          }
        ],
        "Neck pain": [
          {
            "question": "How would the patient describe the nature of the neck pain?",
            "question_loinc_title": "Loinc title",
            "question_loinc_code": "Loinc code",
            "category": "Medical History",
            "code_system": ["SNOMED"],
            "codes": { "SNOMED": "279039007" }
          }
        ],
      }
    `;
  }

  static hpiQuestionsForGivenSymptomPrompt(): string {
    return `
      You are a physician. Your task is to develop the initial subjective assessment for a {age}-year-old {gender} patient presenting with the chief complaint of {chief-complaint}.

      Your objectives:
      - Given the symptom: {symptom}, generate 7 clinically appropriate questions using the PPQRSTA format to clarify the nature and context of this symptom.

      Instructions:
      - Only generate questions, not answers.
      - Do not suggest additional symptoms — focus only on the provided symptom.

      - All questions must:
        -- Be clinically relevant
        -- Be focused on evaluating the specific symptom
        -- Follow the PPQRSTA structure described below
        -- Be concise and medically appropriate
        -- Be phrased in third person, as if asked by a nurse, MA, or physician
        -- Avoid leading phrasing — do not include suggestions, options, or phrasing like “such as”, “for example”, or “etc.”

      - Symptom Naming Requirements:
        -- The symptom you are addressing (i.e., {symptom}) will be a well-defined medical symptom, with no more than two words
        -- You must not generalize or expand it (e.g., don't substitute it with vague terms like "general pain" or "nonspecific issue")


      PPQRSTA Format Guide:

      - Provocation/Palliation
      - Position/Place
      - Quality
      - Radiation
      - Severity
      - Timing
      - Symptoms

      Output Format:

      Return a JSON object, with one key (the given symptom), containing exactly 7 questions in the PPQRSTA structure:

      {
        "{symptom}": [
          "Question 1 related to Provocation/Palliation",
          "Question 2 related to Position/Place",
          "Question 3 related to Quality",
          "Question 4 related to Radiation",
          "Question 5 related to Severity",
          "Question 6 related to Timing",
          "Question 7 related to Symptoms"
        ]
      }
    `;
  }

  /** Last Counter upload: concise encounter summary from referral PDF (max 5 sentences). */
  static lastCounterEncounterSummaryPrompt(): string {
    return `
You are a clinical documentation assistant. Read this medical referral or last-encounter document and write a concise encounter summary.

Requirements:
- No more than 5 sentences.
- Use professional clinical language suitable for EMR documentation.
- Summarize key clinical findings, diagnoses, treatments, and follow-up when present.
- Return only the summary paragraph — no headings, bullets, or JSON.
`.trim();
  }

  /** Established patient New Schedule: 5-sentence summary from prior SOAP note HTML/text. */
  static establishedPatientSoapLastEncounterSummaryPrompt(soapNoteText: string): string {
    return `
You are a clinical documentation assistant. Read this SOAP note from the patient's last encounter and write a concise encounter summary.

SOAP Note:
${soapNoteText}

Requirements:
- No more than 5 sentences.
- Use professional clinical language suitable for EMR documentation.
- Summarize key clinical findings, diagnoses, treatments, and follow-up when present.
- Return only the summary paragraph — no headings, bullets, or JSON.
`.trim();
  }

  static htmlToPlainText(htmlString: string): string {
    if (!htmlString) {
      return "";
    }
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = htmlString;
    return (tempDiv.innerText || tempDiv.textContent || "")
      .replace(/\s+/g, " ")
      .trim();
  }

  /** Alias for {@link htmlToPlainText}. */
  static stripHtmlToPlainText(htmlString: string): string {
    return Utils.htmlToPlainText(htmlString);
  }

  /** Alias for {@link establishedPatientSoapLastEncounterSummaryPrompt}. */
  static lastEncounterSummaryFromSoapNotePrompt(soapNoteText: string): string {
    return Utils.establishedPatientSoapLastEncounterSummaryPrompt(soapNoteText);
  }

  static limitToMaxSentences(text: string, maxSentences = 5): string {
    const trimmed = (text || "").trim();
    if (!trimmed) {
      return "";
    }
    const sentences = trimmed.match(/[^.!?]+[.!?]+(?:\s|$)|[^.!?]+$/g);
    if (!sentences?.length) {
      return trimmed;
    }
    return sentences.slice(0, maxSentences).join(" ").trim();
  }

  static lastEncounterSummaryPrompt(): string {
    return `
      As a physician, you are preparing to document a follow-up visit and must generate a concise yet clinically detailed summary of the patient's last encounter. This summary must clearly outline the relevant clinical events, diagnoses, treatments, and decisions made during the previous visit to support medical continuity, reasoning, and billing compliance.

      Your response must:
      - Include a clear, paragraph-style narrative summarizing the prior visit, focusing on medically relevant events, findings, and interventions.
      - Strictly follow the heading “Last Encounter Summary” without renaming or reformatting.
      - Accurately reflect all clinical details from the prior encounter, including subjective complaints, objective findings, assessments, procedures performed, treatments initiated or adjusted, and follow-up instructions.
      - Highlight medical necessity by connecting the patient’s condition with treatments rendered (e.g., CPTs, medications, imaging).
      - Avoid conversational or non-clinical phrasing: use medically precise, formal language suitable for EMR documentation.
      - Support billing compliance by:
        -- Including ICD-10 diagnoses and their chronicity/status.
        -- Mentioning any performed procedures or services (with laterality if applicable).
        -- Reflecting clinical decision-making and rationale for follow-up.
      - Do not alter formatting. The heading must appear as “Last Encounter Summary” and all content must be documented in a single clinical narrative paragraph.
    `;
  }

  static formatSummary(parsed: any): string {
    // Example: adjust depending on how AI formats responses
    if (typeof parsed === 'string') return parsed;
    if (parsed.summary) return parsed.summary;
    if (parsed.title && parsed.description) {
      return `${parsed.title}: ${parsed.description}`;
    }
    return JSON.stringify(parsed); // fallback
  }

  static fixJson(brokenJson: string) {
    try {
      const repaired = jsonrepair(brokenJson);
      return JSON.parse(repaired);
    } catch (e) {
      console.error("Could not repair JSON:", e.message);
      return null;
    }
  }

  static getLastValidJson<T>(raw: string): T | null {
    let lastArrayStart = -1;
    let bracketDepth = 0;

    for (let i = 0; i < raw.length; i++) {
      const char = raw[i];

      if (char === '[') {
        if (bracketDepth === 0) {
          lastArrayStart = i; // start of a top-level array
        }
        bracketDepth++;
      } else if (char === ']') {
        bracketDepth--;
        // end of a top-level array
        if (bracketDepth === 0 && lastArrayStart !== -1) {
          const candidate = raw.slice(lastArrayStart, i + 1);
          try {
            return JSON.parse(candidate) as T; // parse outermost complete array
          } catch (err) {
            // ignore if parse fails, keep looking
          }
          lastArrayStart = -1;
        }
      }
    }

    return null; // nothing valid found
  }


  static fixManually(brokenJson: string): string {
    return brokenJson
      .replace(/\t/g, " ")             // remove tabs
      .replace(/\r?\n|\r/g, " ")       // remove line breaks
      .replace(/([a-zA-Z0-9_]+):/g, '"$1":') // ensure keys have quotes
      .replace(/,\s*}/g, "}")          // remove trailing commas
      .replace(/,\s*]/g, "]");         // remove trailing commas in arrays
  }

  static cleanTokens(tokens: string[]): string {
    return tokens
      .join("")
      .replaceAll("```html\n", "")
      .replaceAll("json\n", "")
      .replaceAll("json", "")
      .replaceAll("```json\n", "")
      .replaceAll("```", "")
      .replaceAll("`", "")
      .replaceAll("html", "");
  }

  static cleanToken(tokens: string): string {
    return tokens
      .replaceAll("```html\n", "")
      .replaceAll("json\n", "")
      .replaceAll("json", "")
      .replaceAll("```json\n", "")
      .replaceAll("```", "")
      .replaceAll("`", "")
      .replaceAll("html", "");
  }

  static getSuggestedProblemsPrompt(contexts: Context[], chiefComplaint?: string, hpi?: string): string {
    return `
      You are a clinical reasoning assistant. Given a patient's history of present illness (HPI), 
      chief complaint, associated symptoms, diagnostic findings, or transcribed encounter, your 
      task is to generate a detailed but clinically accurate list of diagnoses.

      Follow these rules carefully:

      - If diagnostic findings or an assessment section are provided (e.g., X-ray, MRI, lab results,
        or clinician impression), prioritize those and list confirmed or strongly supported 
        diagnoses first.
      - After confirmed diagnoses, list all possible differential diagnoses (up to 8–10) that are 
        clinically relevant and plausible given the symptoms, findings, or context.
      - Include pain-related diagnoses (M25.561, M25.562, etc.) if pain is explicitly documented as 
        a primary reason for visit or chief complaint symptoms or associated symptoms.
      - If a specific condition (e.g., osteoarthritis) is confirmed by imaging or explicitly 
        mentioned in assessment, mark it as the top diagnosis (highest probability).
      - Avoid listing speculative or unrelated conditions.
      - Each diagnosis (confirmed or differential) must include:
        - ICD-10 code
        - Short description
        - Probability score (0–100%) reflecting confidence
        - Rationale linking to findings, symptoms, or diagnostics
      - Do not prefix or label names with words like “Differential:” or “Confirmed:”. 
        The 'name' field must contain only the clean diagnosis name (e.g., "Rheumatoid arthritis").
      - Output must be a single JSON array — confirmed diagnoses come first, followed by 
        differential diagnoses.

      Output Format (strictly JSON, no extra text):

      [
        {
          "name": "Diagnosis A",
          "icd10": "ICD10-CODE",
          "description": "Short description of the condition.",
          "probabilityScore": "95%",
          "rationale": "Explain why this diagnosis fits, referencing findings or imaging confirmation."
        },
        {
          "name": "Diagnosis B",
          "icd10": "ICD10-CODE",
          "description": "Short description of the condition.",
          "probabilityScore": "85%",
          "rationale": "Explain why this is included, referencing key symptoms or history."
        }
      ]

      ${chiefComplaint ? `Chief Complaint:\n${chiefComplaint}\n\n` : ''}
      ${hpi ? `History of Present Illness:\n${hpi}\n\n` : ''}

      Now here is the list of context summaries (which may include exam findings, imaging, 
      assessments, or associated symptoms):
      ${contexts.map((m) => `${m.title}\n${m.summary}`).join("\n")}

      Important Notes:

      - When imaging (e.g., X-ray, MRI) confirms a finding (e.g., osteoarthritis), give it a 
        probabilityScore ≥ 90% and list it first.
      - When pain is localized and chronic (e.g., "pain in left knee for years"), include pain 
        diagnosis codes (M25.561, M25.562) with high probability.
      - Differential diagnoses must come after confirmed ones but should include all plausible 
        alternatives (musculoskeletal, inflammatory, neurologic, referred pain, etc.).
      - Always return only pure JSON — no explanations, no comments, no markdown.
    `;
  }

  static getGenerateContextSummaryFromHtmlTemplatePrompt(context: any, linkedContext: string[] = []): string {
    const linkedContextText = linkedContext && linkedContext.length > 0 
      ? linkedContext.join('\n        ') 
      : '';
    
    const hasLinkedContext = linkedContext && linkedContext.length > 0;
    
    return `
      You will receive a medical form in raw HTML format.

      The form type is ${context.title} (e.g., Medical History, Social History, Family History, Review of Systems, etc.),
      and it belongs to one of the SOAP note sections: ${context.templateEntity.templateSubTypes.join(', ')}
      (e.g., Subjective, Objective, Assessment, or Plan).

      ${hasLinkedContext ? `**IMPORTANT - Patient Context Information (MUST be included in output):**  
        ${linkedContextText}
        
        You MUST start your summary by incorporating this patient context information.
        - Use the EXACT age and gender if provided in Demographics (e.g., "45-year-old male...")
        - Reference any relevant findings from Subjective, Objective, Assessment, or Plan sections
        - Do NOT skip, ignore, or omit this context - it MUST appear in your output` : ''}

      Your task:
      Analyze the raw HTML in {RAW_HTML} and extract only clinically relevant information related to ${context.title}.
      Ignore all unrelated sections such as encounter metadata, and any layout or styling code.
      Focus only on the form's meaningful medical content — checked boxes, text inputs, and written notes that apply to the ${context.title}.

      Then, summarize the findings in a natural clinical voice, as if a nurse or medical assistant were speaking directly with the patient and recording their responses.

      Output requirements:
      ${hasLinkedContext ? '- **FIRST**: Start with the patient context (demographics, relevant SOAP findings) provided above.' : ''}
      - Write a single, coherent paragraph (2–4 sentences).
      - Use plain English — no HTML, lists, or structured formatting.
      - Avoid phrasing like "The form indicates…" or "The section reports…".
        Instead, phrase naturally, e.g.,
          "${hasLinkedContext ? '45-year-old male with history of knee pain. ' : ''}Patient reports occasional alcohol use and smokes daily. Denies vaping or drug use. Works as a pilot and lives with family."
      - Include only clinically relevant information related to ${context.title}.
      - If no relevant data is found, return nothing.

      Analyze the HTML content below and produce your summary:
      {RAW_HTML}
    `;
  }

  static getGenerateWOMACContextSummaryPrompt(context: any, linkedContext: string[] = []): string {
    const linkedContextText = linkedContext && linkedContext.length > 0 
      ? linkedContext.join('\n  ') 
      : 'No additional context provided';
    
    return `
      You will receive a medical form in raw HTML format.

The form type is WOMAC (WOMAC - Western Ontario and McMaster Universities Arthritis Index),
and it belongs to one of the SOAP note sections: ${context.templateEntity.templateSubTypes.join(', ')}
(e.g., Subjective, Objective, Assessment, or Plan).

**Optional Context (If Provided):**  
  ${linkedContextText}
  
  # Any relevant patient information, such as demographics, medical history, or findings from Subjective, Objective, Assessment, or Plan (S/O/A/P).  
  # Context should be used **only to enhance the clinical relevance, accuracy, and quality** of the AI-generated output.
  # You MUST use the EXACT age and gender if available.
  # Do NOT change, estimate, or make up different demographics
  # IMPORTANT: Include this context information in your summary if it's relevant.

Your task:
Analyze the raw HTML in {RAW_HTML} and extract only clinically relevant information related to ${context.title}.
Ignore all unrelated sections such as demographics, vital signs, encounter metadata, and any layout or styling code.
Focus only on the form's meaningful medical content — checked boxes, text inputs, and written notes that apply to the ${context.title}.

Then, summarize the findings in a natural clinical voice, as if a nurse or medical assistant were speaking directly with the patient and recording their responses.

Output requirements:
- Write a single, coherent paragraph (2–4 sentences).
- Use plain English with minimal HTML (only use <b> for bold text).
- Avoid phrasing like "The form indicates…" or "The section reports…". Instead, phrase naturally, e.g.,
  "Patient reports occasional alcohol use and smokes daily. Denies vaping or drug use. Works as a pilot and lives with family."
- Include only clinically relevant information related to WOMAC.
- At the end of the summary:
  - Calculate and include the total WOMAC score in bold and in a new line.
  - Also include the interpretation of the score in parentheses, for example:
    You MUST render the score line using an inline style that enforces 14px font size so stylesheet overrides won't change it. Output the score line exactly using this pattern (replace the numeric score and interpretation with the computed values):
    <b style="font-size:14px !important;"><br/><br/>WOMAC Score: 55 – Indicates moderate functional impairment.</b>
    - Use these score ranges for interpretation:
      - 0–20 → Minimal functional impairment
      - 21–40 → Mild functional impairment
      - 41–60 → Moderate functional impairment
      - 61–80 → Severe functional impairment
      - 81–96 → Extreme functional impairment
- If no relevant data is found, return nothing.

Analyze the HTML content below and produce your summary:
{RAW_HTML}

    `;
  }

  static getGenerateHistoryContextSummaryPrompt(context: any, linkedContext: string[] = []): string {
    const linkedContextText = linkedContext && linkedContext.length > 0 
      ? linkedContext.join('\n  ') 
      : 'No additional context provided';
    
//     return `
//       You will receive a comprehensive medical history form in structured text format.

//   The form type is ${context.title}, containing patient medical history data,
//   and it belongs to one of the SOAP note sections: ${context.templateEntity.templateSubTypes.join(', ')}
//   (e.g., Subjective, Objective, Assessment, or Plan).

//   **Optional Context (If Provided):**  
//   ${linkedContextText}
  
//   # Any relevant patient information, such as demographics, medical history, or findings from Subjective, Objective, Assessment, or Plan (S/O/A/P).  
//   # Context should be used **only to enhance the clinical relevance, accuracy, and quality** of the AI-generated output.
//   # You MUST use the EXACT age and gender if available.
//   # Do NOT change, estimate, or make up different demographics

//   Your task:
//   Analyze the structured text data in {INPUT_DATA}
  

//   And extract **ALL clinically relevant, affirmative findings** across ALL categories of medical history data.
//   The data is formatted as:
//   - [Type] Question: [question text]
//   - Response: [yes/no or other response]
//   - Additional Notes: [details | When: date/time] or [details]
//   - Follow-up Details: [key: value]
//   - [Section] Additional Comments: [overall comments for the entire section]

//   Focus on meaningful medical content — responses, notes, details, and section-level comments that reflect true medical findings.

// **CRITICAL**: You must categorize the data correctly into the appropriate section. DO NOT mix data from different categories.

// **IMPORTANT PATTERNS TO LOOK FOR**:
// - **Immunization**: Look for vaccine names, immunization entries with dates (often in pairs: "vaccine name" and "when")
// - **Surgical History**: Look for surgical procedures with dates or years
//   - **CRITICAL EXCEPTION**: For questions about "blood transfusion" or "adverse reaction to anesthesia", include BOTH "Response: yes" AND "Response: no". Format as "Question __ yes" or "Question __ no"
// - **Implants**: Look for medical devices, prosthetics, pacemakers, joint replacements with dates
// - **Diagnostics**: Look for test results, imaging studies, lab work, diagnostic procedures with dates or findings
// - **Family History**: Look for "Additional Notes: details | When: date" format - extract both details and timing
// - Look for "Response: yes" or "Response: no" to determine if data should be included
// - Look for "Additional Notes:" followed by condition details and optional timing information
// - Look for "Follow-up Details:" with key-value pairs for additional context
// - Look for "[Section] Additional Comments:" which provides overall comments for an entire section - include these as additional context in the relevant section

// Output requirements:
// - Use valid, clean HTML.
// - Structure the output under **seven mandatory sections**, each with a bold heading:
//   1. <b>Medical Conditions</b> - Patient's current and past medical conditions (e.g., hypertension, diabetes, asthma)
//   2. <b>Immunization</b> - Vaccination records and dates
//   3. <b>Surgical History</b> - Past surgical procedures and dates
//   4. <b>Implants</b> - Medical devices, prosthetics, and implants
//   5. <b>Diagnostics</b> - Recent tests, imaging, and diagnostic procedures
//   6. <b>Family History</b> - Medical conditions in family members only (mother, father, siblings)
//   7. <b>Social History</b> - Lifestyle factors (smoking, alcohol, exercise, occupation)
// - Under each heading, list each item on a **separate line** (not as a bullet).
// - **Keep each section strictly separate** - do not mix family history with medical conditions, or any other combinations.
// - Use the following format for every line:
  
//   Condition __ [Comment or details]

//   Example:
//   Asthma / COPD / Lung disease __ On inhalers, controlled  
//   Diabetes __ Age of onset: 45
//   Arthritis / Joint disease __ Osteoarthritis, Age of onset: 60  
//   Gastrointestinal disease __ GERD, onset: ~15 years ago

// Formatting Rules:
// - Always use "__" (two underscores) as the separator between the item name and its detail/comment.
// - For Medical Conditions, Family History, and Social History: ONLY include entries where comments or additional information exist.
// - **For Medical Conditions**: 
//   - When you see "Follow-up Details" with entries like "Diabetes: 45", interpret the number as "Age of onset" and format as: "Diabetes __ Age of onset: 45"
//   - **CRITICAL**: If you encounter "Additional Notes" within a Medical Conditions question, this should be displayed AFTER the closing </ul>
//   - Example: If a question has conditions "Hypertension, Diabetes" with follow-up details AND "Additional Notes: blood clot", output should be:
//     <li>Hypertension __ [details if any]</li>
//     <li>Diabetes __ [details if any]</li>
//     <b>-Additional comments:</b>blood clot
//     </ul>
// - **For Family History**: Include additional details and timing when present. 
//   - If you see "Additional Notes: details | When: date", format as: "Condition __ details, When: date"
//   - If you see just "Additional Notes: details", format as: "Condition __ details"
// - For Immunization, Surgical History, Implants, and Diagnostics: Include ALL entries that have data (dates, procedure names, test results, etc.) even if minimal detail is provided. The entry itself is valuable clinical data.
// - If an item has a date but no other details, still include it (e.g., "Flu vaccine __ 2024").
// - **SPECIAL EXCEPTION for Surgical History**: For questions about "blood transfusion" or "adverse reaction to anesthesia", INCLUDE both "yes" and "no" responses. Format as: "Question __ yes" or "Question __ no"
// - Exclude any items where the patient denies or selects "No", EXCEPT for the special surgical history questions mentioned above.
// - Exclude any text containing "none", "negative", "denies", "no", "n/a", "unknown", or placeholders, EXCEPT when it's a direct "yes" or "no" response to blood transfusion or adverse reaction to anesthesia questions.
// - Include only meaningful, affirmative findings.
// - **IMPORTANT**: Correctly identify and categorize each piece of data:
//   * Medical Conditions → Only patient's own conditions (NOT family members)
//   * Immunization → Vaccines, immunizations, vaccination records
//   * Surgical History → Surgical procedures, operations
//   * Implants → Medical devices, prosthetics, pacemakers, joint replacements
//   * Diagnostics → Lab tests, imaging (MRI, CT, X-ray), diagnostic procedures, test results
//   * Family History → ONLY conditions affecting family members (mother, father, siblings, etc.)
//   * Social History → ONLY lifestyle and social factors (smoking, alcohol, drugs, occupation, exercise)
// - **CRITICAL TEXT PATTERNS**: Look for:
//   * "Additional Notes:" followed by details and optional "| When: date" timing
//   * "Follow-up Details:" with key-value pairs (e.g., "Diabetes: 45")
//   * "Response: yes" indicates positive findings to include
//   * "Response: no" indicates negative findings to exclude EXCEPT for blood transfusion and adverse reaction to anesthesia questions
//   * **SPECIAL SURGICAL HISTORY PATTERN**: Questions containing "blood transfusion" or "adverse reaction" and "anesthesia" should be included with BOTH "Response: yes" AND "Response: no" - format as "Question text __ yes" or "Question text __ no"
//   * Multiple entries separated by semicolons for Immunization, Surgical History, Implants
//   * Structured format: "[Type] Question:" followed by response and notes
//   * "[Section] Additional Comments:" provides additional context for the entire section - incorporate these comments into the relevant section's summary
// - **DO NOT SKIP DATA**: If you find ANY vaccine name, surgical procedure, implant, diagnostic test, or family history condition with data, include it in the appropriate section
// - If a section has NO valid findings after thorough search, display exactly:
//   "No relevant findings or data reported."
// - Use concise, clinical phrasing throughout.
// - Return **valid HTML only**, no extra text, no markdown.
// - Each section must be independent - never combine data from multiple categories into one section.

// Example Output Format:

// <b style="font-size: 14px;">Medical Conditions</b>
// <ul>
//   <li>Asthma / COPD / Lung disease __ On inhalers, controlled</li>
//   <li>Diabetes __ Age of onset: 45</li>
//   <li>Arthritis / Joint disease __ Osteoarthritis, Age of onset: 60</li>
//   <li>Gastrointestinal disease __ GERD, onset: ~15 years ago</li>
//   <b>- Additional comments:</b> Patient notes history of blood clot
// </ul>

// <b style="font-size: 14px;">Immunization</b>
// <ul>
//   <li>Flu vaccine __ Received annually, last dose 10/2024</li>
//   <li>COVID-19 vaccine __ Pfizer, 3 doses completed</li>
// </ul>

// <b style="font-size: 14px;">Surgical History</b>
// <ul>
//   <li>Appendectomy __ Performed in 2015</li>
//   <li>Knee arthroscopy __ Right knee, 2020</li>
//   <li>Blood transfusion __ yes</li>
//   <li>Adverse reaction to anesthesia __ no</li>
// </ul>

// <b style="font-size: 14px;">Implants</b>
// <ul>
//   <li>Pacemaker __ Dual chamber, implanted 2018</li>
//   <li>Hip replacement __ Left hip, titanium, 2019</li>
// </ul>

// <b style="font-size: 14px;">Diagnostics</b>
// <ul>
//   <li>MRI __ Lumbar spine, 2023, showed disc herniation</li>
//   <li>CT scan __ Chest, 2022, normal findings</li>
// </ul>

// <b style="font-size: 14px;">Family History</b>
// <ul>
//   <li>Hypertension __ Father has hypertension, __ 2020</li>
//   <li>Diabetes __ Mother, Type 2, __ 5 years ago</li>
//   <li>Heart disease __ Family history of heart disease</li>
// </ul>

// <b style="font-size: 14px;">Social History</b>
// <ul>
//   <li>Tobacco use __ Smokes daily</li>
//   <li>Alcohol use __ Occasional use</li>
//   <li>Exercise __ Regular walking</li>
// </ul>

// **CRITICAL REMINDER**: 
// - You MUST output ALL seven sections even if some are empty.
// - THOROUGHLY search the HTML for data in ALL categories: Medical Conditions, Immunization, Surgical History, Implants, Diagnostics, Family History, and Social History.
// - **PAY SPECIAL ATTENTION** to finding:
//   * For Medical Conditions: 
//     - When you see "Follow-up Details" like "Diabetes: 45" or "Hypertension: 60", these numbers are "Age of onset" - format as "Diabetes __ Age of onset: 45"
//   * ANY vaccine names, immunization records → Immunization section
//   * ANY medical devices, prosthetics, pacemakers, joint replacements → Implants section  
//   * ANY lab results, imaging studies, diagnostic tests, test names with dates → Diagnostics section
//   * For Family History: When you see "Additional Notes: details | When: date", include both the details and timing in format: "Condition __ details, When: date"
//   * **CRITICAL FOR SURGICAL HISTORY**: Questions about "blood transfusion" or "adverse reaction to anesthesia" MUST be included with their yes/no response - format as "Blood transfusion __ yes" or "Adverse reaction to anesthesia __ no"
// - Do NOT skip Immunization, Implants, or Diagnostics sections - they are just as critical as other sections.
// - Even if you find just one vaccine, one implant, or one diagnostic test, include it in the output.
// - Do NOT put family member conditions in Medical Conditions - they belong ONLY in Family History.
// - For blood transfusion and adverse reaction to anesthesia questions, include them REGARDLESS of whether the response is yes or no.
// - If you're unsure where something belongs, include it rather than skip it.

// Analyze the structured text data below and produce your summary:
// {INPUT_DATA}

//     `;
return `You will receive a comprehensive medical history form in structured text format.

The form type is ${context.title}, containing patient medical history data,
and it belongs to the SOAP note sections: ${context.templateEntity.templateSubTypes.join(', ')}.

Optional Context:
${linkedContextText}

Task:
Analyze the structured text data in {INPUT_DATA} and extract all clinically relevant affirmative findings. Place each finding under the correct category.

Output requirements (critical):
- Return valid HTML only (no markdown).
- Use one bold category heading per section, then a <ul> with one <li> per finding.
- Do NOT repeat the category label on every bullet. The heading appears once; each <li> contains only the clinical detail.
- Omit sections that have no affirmative findings.
- Use these category headings when applicable:
  <b>Medical conditions:</b>
  <b>Immunization:</b>
  <b>Surgical history:</b>
  <b>Implants:</b>
  <b>Diagnostics/Screening:</b> (use for labs, imaging, screening tests)
  <b>Family history:</b>
  <b>Social history:</b>

Example:
<b>Medical conditions:</b>
<ul>
<li>History of hypertension.</li>
<li>History of arthritis.</li>
</ul>
<b>Diagnostics/Screening:</b>
<ul>
<li>Mammogram reported normal.</li>
<li>Colonoscopy reported normal.</li>
</ul>

Rules:
- Only include meaningful, positive findings.
- Exclude negative answers except surgical questions about blood transfusion or adverse reaction to anesthesia (include yes and no for those).
- Use concise clinical phrasing.
- Interpret follow-up numbers in medical conditions as age of onset where applicable.
- Include section-level additional comments in the relevant section (not as a repeated heading on every line).
- Never hallucinate or invent findings.
- Keep family history separate from the patient's own medical conditions.

Analyze the structured text data below and produce the grouped HTML summary:

{INPUT_DATA}


`;
  }

  static getGenerateLowerExtremityExamContextSummaryPrompt(context: any, linkedContext: string[] = [], selectedSide: string = 'both'): string {
    const linkedContextText = linkedContext && linkedContext.length > 0 
      ? linkedContext.join('\n        ') 
      : 'No additional context provided';
    
    // Determine which sides to generate based on selectedSide parameter
    const generateRight = selectedSide === 'right' || selectedSide === 'both';
    const generateLeft = selectedSide === 'left' || selectedSide === 'both';
    
    // return `You are a clinical reasoning engine specialized in musculoskeletal exams.

    // The form type is ${context.title} (Lower Extremity Exam),
    //   and it belongs to one of the SOAP note sections: ${context.templateEntity.templateSubTypes.join(', ')}
    //   (e.g., Subjective, Objective, Assessment, or Plan).

    //   Context Title: Lower Extremity Exam

    //   Your task:
    //   Analyze the following filled HTML form representing a lower extremity physical examination.
    //   Extract only clinically relevant findings and generate the output in the **same structured HTML template** shown below.

    //   INSTRUCTIONS:
    //   - Follow the exact HTML structure and formatting provided below.
    //   - Replace bracketed placeholders (like [bilaterally/left/right]) and blanks (___) with the appropriate values extracted from the HTML input.
    //   - Preserve the same tag structure and bold section headings (<b>...</b>).
    //   - For the Range of Motion (ROM) section, use an HTML table as shown, filling in the numeric values for Right Knee, Left Knee, and Normal Range. Put black border to the table rows and columns.
    //   - For Special Tests, mark the correct option as “Positive” or “Negative” (do not leave both). Include positive checkbox with Positive value and negative checkbox with Negative value.
    //   - For any missing data, leave the field blank but retain the placeholder (e.g., “___°” or “☐ Positive ☐ Negative”).
    //   - Do NOT summarize into sentences or paragraphs beyond what fits in the given tags.
    //   - Do not remove any css or formating from the html I gave including the table css.
    //   - Output must be based on the html I gave.
    //   - Exactly same html structure and formatting should be used.

    //   OUTPUT FORMAT (Template):

    //   <b>Inspection:</b>
    //   <p>[inspection findings]</p>

    //   <b>Palpation:</b>
    //   <p>[palpation findings]</p>

    //   <b>Range of Motion (ROM):</b>
    //    <table class="rom-table" border="1" cellpadding="6" cellspacing="0" width="100%">
    //       <thead>
    //         <tr>
    //           <th style="border: 1px solid #000 !important;">Movement</th>
    //           <th style="border: 1px solid #000 !important;">Right Knee</th>
    //           <th style="border: 1px solid #000 !important;">Left Knee</th>
    //           <th style="border: 1px solid #000 !important;">Normal Range</th>
    //         </tr>
    //       </thead>
    //       <tbody>
    //         <tr>
    //           <td style="border: 1px solid #000 !important;">Flexion</td>
    //           <td style="border: 1px solid #000 !important;">[right flexion value]</td>
    //           <td style="border: 1px solid #000 !important;">[left flexion value]</td>
    //           <td style="border: 1px solid #000 !important;">135°</td>
    //         </tr>
    //         <tr>
    //           <td style="border: 1px solid #000 !important;">Extension</td>
    //           <td style="border: 1px solid #000 !important;">[right extension value]</td>
    //           <td style="border: 1px solid #000 !important;">[left extension value]</td>
    //           <td style="border: 1px solid #000 !important;">0°</td>
    //         </tr>
    //       </tbody>
    //     </table>

    //   <br/>
    //   <b>Strength Testing (0–5 scale):</b>
    //   <p>Quadriceps: [value]  Hamstrings: [value]  Gastrocnemius: [value]</p>

    //   <b>Special Tests:</b>
    //   <ul>
    //     <li>McMurray: [Positive or Negative]</li>
    //     <li>Lachman: [Positive or Negative]</li>
    //     <li>Anterior drawer: [Positive or Negative]</li>
    //     <li>Varus/Valgus stress: [Stable or Laxity present]</li>
    //     <li>Patellar grind: [Positive or Negative]</li>
    //   </ul>

    //   <b>Gait:</b>
    //   <p>[gait findings]</p>

    //   <b>Neurovascular:</b>
    //   <p>[neurovascular findings]</p>

    //   Now analyze the input HTML form and generate the structured HTML output accordingly.

    //   HTML INPUT:
    //   {RAW_HTML}

    // `;

    return `You are a clinical reasoning engine specialized in musculoskeletal exams.

      The form type is ${context.title} (Lower Extremity Exam),
      and it belongs to one of the SOAP note sections: ${context.templateEntity.templateSubTypes.join(', ')}
      (e.g., Subjective, Objective, Assessment, or Plan).

      Context Title: Lower Extremity Exam
      
      Your task:
      Analyze the following filled HTML form representing a lower extremity physical examination.
      Extract only clinically relevant findings and generate the output in the **same structured HTML template** shown below.

      INSTRUCTIONS:
      - Before the structured HTML sections, generate a brief **2–3 sentence summary** that concisely describes the overall findings.
        - The summary should highlight any key abnormalities, differences between right and left sides (if both are selected), or note that findings are within normal limits.
        - Keep the summary clinically focused (no formatting or headings).
        - Do NOT include HTML tags or bullet points in the summary — just plain text.
        - This summary must Include the **Optional Context** if provided.
        - This summary should appear **above** the structured HTML output.
      - Then generate structured HTML output based on the selected side(s):
        ${generateRight && generateLeft ? '1. Entire structured HTML output for the **Right** side.\n        2. Entire structured HTML output for the **Left** side.' : generateRight ? '1. Entire structured HTML output for the **Right** side ONLY.' : '1. Entire structured HTML output for the **Left** side ONLY.'}
      - Each side must include **all sections** (Inspection, Palpation, Range of Motion, Strength Testing, Special Tests, Gait, Neurovascular).
      - Do NOT combine both sides into one table or paragraph.
      - **IMPORTANT**: Only generate output for the side(s) that are selected. If only Right is selected, generate ONLY Right side. If only Left is selected, generate ONLY Left side. If Both is selected, generate both sides.
      - Maintain identical formatting, CSS, and HTML tag structure for both sections.
      - For any missing data, leave placeholders (e.g., “___°” or “☐ Positive ☐ Negative”).
      - For any positive test, include the comment value in the test name.
      - For any positive test don't say Positive. Just show {Name of the test}, {primary focus text} {comment value}.
      - Use black borders for all ROM table cells.
      - Do NOT summarize findings into text beyond the HTML fields, except for the initial summary.
      - Do NOT modify or remove any HTML or CSS formatting provided.
      - Output must be based strictly on the given input HTML form.

      OUTPUT FORMAT (Generate in this order)
      1.**Optional Context (If Provided):**  
        ${linkedContextText}
        
        # Any relevant patient information, such as demographics, medical history, or findings from Subjective, Objective, Assessment, or Plan (S/O/A/P).  
        # Context should be used **only to enhance the clinical relevance, accuracy, and quality** of the AI-generated output.
        # You MUST use the EXACT age and gender if available.
        # Do NOT change, estimate, or make up different demographics
        # IMPORTANT: Include this context information in your clinical summary if it's relevant.
        
        Short 2–3 sentence clinical summary (plain text) that incorporates the optional context above. Then a space of a line using <br/><br/>
      ${generateRight && generateLeft ? '2. Full structured HTML for RIGHT\n      3. Full structured HTML for LEFT' : generateRight ? '2. Full structured HTML for RIGHT ONLY' : '2. Full structured HTML for LEFT ONLY'}

      ${generateRight ? `<h3>RIGHT</h3>

      <b>Inspection:</b>
      <p>[inspection findings]</p>

      <b>Palpation:</b>
      <p>[palpation findings]</p>

      <b>Range of Motion (ROM):</b>
      <table class="rom-table" border="1" cellpadding="6" cellspacing="0" width="100%">
        <thead>
          <tr>
            <th style="border: 1px solid #000 !important;">Movement</th>
            <th style="border: 1px solid #000 !important;">Value</th>
            <th style="border: 1px solid #000 !important;">Normal Range</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td style="border: 1px solid #000 !important;">Flexion</td>
            <td style="border: 1px solid #000 !important;">[right flexion value]</td>
            <td style="border: 1px solid #000 !important;">135°</td>
          </tr>
          <tr>
            <td style="border: 1px solid #000 !important;">Extension</td>
            <td style="border: 1px solid #000 !important;">[right extension value]</td>
            <td style="border: 1px solid #000 !important;">0°</td>
          </tr>
        </tbody>
      </table>

      <br/>
      <b>Gait:</b>
      <p>[gait findings]</p>

      <b>Joint Swelling:</b>
      <p>[if Yes then Sweling is present <br> Severity:<ul><li>[edema findings]</li></ul> or No swelling, No pit.]</li></p>

      <b>Strength Test (0–5 scale):</b>
      <p>Quadriceps: [value]  Hamstrings: [value]  Gastrocnemius: [value]</p>

      <b>Neurovascular:</b>
      <p>[neurovascular findings]</p>

      <b>Orthopedic Exam:</b>
      <ul>
        <li>Lachman: [if Positive then [Lachman, ACL integrity [comment value]] or Negative]</li>
        <li>Anterior Drawer: [if Positive then Anterior Drawer, ACL integrity [comment value] or Negative]</li>
        <li>Posterior Drawer: [if Positive then Posterior Drawer, PCL integrity [comment value] or Negative]</li>
        <li>Valgus Stress: [if Positive then Valgus Stress, Medial collateral ligament (MCL) [comment value] or Negative]</li>
        <li>Varus Stress: [if Positive then Varus Stress, Lateral collateral ligament (LCL) [comment value] or Negative]</li>
        <li>McMurray's: [if Positive then McMurray's, Meniscus tears [comment value] or Negative]</li>
      </ul>` : ''}
      
      ${generateLeft ? `<h3>LEFT</h3>
      (Repeat the exact same structure for the Left side)` : ''}

      Now analyze the input HTML form and generate the summary and structured HTML output accordingly.

      HTML INPUT:
      {RAW_HTML}
    `;
  }

  

  static getGenerateVitalsContextSummaryPrompt(context: any, linkedContext: string[] = []): string {
    const linkedContextText = linkedContext && linkedContext.length > 0 
      ? linkedContext.join('\n    ') 
      : 'No additional context provided';
    
    return `You are a clinical reasoning engine specialized in medical documentation.

    The form type is ${context.title} (Vitals),
    and it belongs to one of the SOAP note sections: ${context.templateEntity.templateSubTypes.join(', ')}
    (e.g., Subjective, Objective, Assessment, or Plan).

    **Optional Context (If Provided):**  
    ${linkedContextText}
    
    # Any relevant patient information, such as demographics, medical history, or findings from Subjective, Objective, Assessment, or Plan (S/O/A/P).  
    # Context should be used **only to enhance the clinical relevance, accuracy, and quality** of the AI-generated output.
    # You MUST use the EXACT age and gender if available.
    # Do NOT change, estimate, or make up different demographics

    Your task:
    Analyze the following filled HTML form containing patient vitals.
    Extract the vital values and output **only** a single clean HTML table, exactly as shown below.
    Do NOT include any extra text, titles, explanations, HTML, HEAD, or BODY tags.
    Output must contain only the <table> and, if applicable, the optional Comments section.

    Requirements:
    - Keep the same column headers: HT, WT, TEMP, HR, RR, BP, SAO2.
    - Fill the corresponding cells with extracted numeric or text values. Leave empty if not found.
    - Show all table borders clearly (use inline CSS with 'border: 1px solid #000;').
    - The output must be valid standalone HTML (just the table, not a full HTML document).
    - If the Comments or Additional Notes field is empty, missing, or contains only whitespace, omit the entire Comments section (including the <br/>, <b>Comments:</b>, and <p> tags).
    - If Comments exist, include them **after the table**, formatted as shown below.

    Output Format Example:

    <table border="1" cellpadding="6" cellspacing="0" width="100%">
      <thead>
        <tr>
          <th style="border: 1px solid #000;">HT</th>
          <th style="border: 1px solid #000;">WT</th>
          <th style="border: 1px solid #000;">TEMP</th>
          <th style="border: 1px solid #000;">HR</th>
          <th style="border: 1px solid #000;">RR</th>
          <th style="border: 1px solid #000;">BP</th>
          <th style="border: 1px solid #000;">SAO2</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td style="border: 1px solid #000;">[ht value]</td>
          <td style="border: 1px solid #000;">[wt value]</td>
          <td style="border: 1px solid #000;">[temp value]</td>
          <td style="border: 1px solid #000;">[hr value]</td>
          <td style="border: 1px solid #000;">[rr value]</td>
          <td style="border: 1px solid #000;">[bp value]</td>
          <td style="border: 1px solid #000;">[sao2 value]</td>
        </tr>
      </tbody>
    </table>

    [Only if comments exist:]
    <br/>
    <b>Comments:</b>
    <p>[comments text]</p>

    If comments are empty, end output after the closing </table> tag.

    HTML INPUT:
    {RAW_HTML}
    
    `;
  }

  static getGenerateContextSummaryFromPromptOnlyTemplate(): string {
    return `
      You will receive a mixed block of text that may include instructional content
      (e.g., “Context for the Physician/AI”, “Prompt Structure”, numbered steps)
      and a section labeled “— Template Variables Used —” followed by clinical fields
      such as Chief Complaint, HPI, Active Problems, and possibly other items
      (e.g., Vitals, Exam, ROS, Medications, Allergies, Past History, Labs, Imaging).

      Your task is to ignore all instructional/scaffolding text and analyze only the
      clinical facts contained in the “Template Variables Used” section and any clearly
      labeled field lines of the form 'Field: value'. Remove any HTML tags or attributes
      if present.

      From this information, generate a single, concise paragraph in plain English
      that summarizes:
        - Patient identifiers if provided (name/age/sex), date(s) of service or event,
          provider/location if present.
        - Chief complaint and a tight HPI synthesis (onset, context, course, modifiers,
          associated symptoms).
        - Active problems/PMH relevant to today’s issue.
        - Vital signs with values (normalize units), and pertinent ROS/Exam findings if present.
        - Any specific quantitative details (e.g., size, stage, number, laterality)
          and key risk factors.

      Rules & formatting:
        - Output must be exactly one coherent paragraph (no lists, tables, headings, or JSON).
        - Omit fields that are missing or obviously placeholder/implausible
          (e.g., “0-year-old” → treat as age not stated).
        - Prefer explicit values over implied text; do not invent information.
        - Normalize dates to “Month D, YYYY” where possible; convert units to standard clinical notation.
        - Keep tone clinical and succinct; focus on information that changes assessment or management.
        - If conflicting data appears, prioritize the most specific/recent statement
          and note uncertainty briefly (“age not stated” or “date unspecified”) instead of guessing.

      I will paste the mixed content directly after this prompt.
      Please return only the final paragraph.
`;
  }

  /** Default AI prompt for new Form / Context / Order / Document templates (not care plans). */
  static getDefaultTemplatePrompt(): string {
    return (
      "You are a clinical documentation assistant. Summarize the provided context for insertion into an encounter note. " +
      "Rules: -Remove all Markdown bold formatting (double asterisks …) and return the text as plain text. " +
      "Do not change wording—only remove the ** symbols. -Strip all asterisks used for emphasis. " +
      "Keep punctuation, line breaks, and wording the same. -Convert this to non-bold text (no ** anywhere). " +
      "Keep headings and colons."
    );
  }

  static getSuggestedServicesPrompt(
    contexts: Context[],
    problemsList: any[],
    patientAge: string,
    visitType: string,
    texanomy: string
  ): string {
    const problemsJson = JSON.stringify(problemsList, null, 2);
    const contextsJson = JSON.stringify(contexts, null, 2);


    return `
      You are a medical coding assistant who specializes in coding for ${texanomy}. Based on patient problems,
      context summaries, and visit details, suggest clinically appropriate
      and compliant CPT® / HCPCS-coded services for each problem.

      INPUTS PROVIDED:
      --------------------------------------------------------------
      - problemsJson: ${problemsJson},
      - contextsJson: ${contextsJson},
      - visitType: ${visitType}
      - patientAge: ${patientAge}

      INSTRUCTIONS:
      --------------------------------------------------------------
      1. Extract key symptoms, signs, and contextual factors relevant
          to each problem.
      2. Generate candidate CPT/HCPCS codes including:
          - Consultations (based on new vs. established)
          - Office visits and preventive physicals (age-dependent)
          - Procedures, labs, injections, imaging, therapies, devices
      3. Include medically relevant modifiers for every service
          (e.g., 25, 59, 26, 52, 76, XS)
      4. Flag codes that require prior authorization with
          "authRequired": true
      5. Provide a short rationale (≤35 words) explaining why the
          service is appropriate for the problem and patient context
      6. Assign a confidence level: "High", "Moderate", "Low"
      7. Output only valid JSON; no additional text.

      OUTPUT FORMAT:
      --------------------------------------------------------------
      [
        {
          "displayName": "Office/outpatient visit, est pt, moderate MDM",
          "cptCode": "99214",
          "category": "E/M | Procedure | Imaging | Lab | Therapy | Vaccine | Device/Supply | Other",
          "siteOfService": "Office | Outpatient Hospital | ASC | Telehealth | Home",
          "associatedProblem": {
            "id": "4dec93aa-446d-437f-9831-143bf0881cc2",
            "type": "Chronic",
            "status": "Unchanged",
            "isVerified": true,
            "isNew": false,
            "isChecked": false,
            "icdCode": "H10.1",
            "description": "Acute atopic conjunctivitis"
          },
          "requiredModifiers": ["25","59","26","52","XS"],
          "addOnCodes": ["string if applicable"],
          "bundlingNotes": "string if applicable",
          "authRequired": true | false,
          "globalPeriod": "0 | 10 | 90 | N/A",
          "medicalNecessityLink": "ICD-10 or indication phrase",
          "mueOrFreqLimits": "string or null",
          "units": 1,
          "rationaleShort": "≤35 words explaining why this service is appropriate given goals, evidence, patient factors, risks, and SDM",
          "confidence": "High | Moderate | Low"
        }
      ]

      NOTES:
      --------------------------------------------------------------
      - Automatically select consult codes based on visitType and patientAge
        (e.g., 99385–99387 for new patients, 99395–99397 for established patients)
      - Include preventive physicals, wellness visits, and office visits as appropriate
      - Only return CPT/HCPCS codes (never ICD/diagnosis codes)
      - Ensure every code has modifiers and rationale
      - Include authorization flag for codes likely requiring prior authorization
      - Do NOT remove or alter original problem IDs or metadata
    `;

    // return `
    //     You are a medical coding assistant.

    //     Based on the following problems and patient context summaries, suggest appropriate services for each problem. For every suggested service, return:

    //     - 'displayName': Name of the service.
    //     - 'cptCode': The CPT code associated with the problem.
    //     - 'associatedProblem': The problem object from the original problems list that this service is related to.
    //     - 'modifiers': The modifiers associated with the service.
    //     - 'description': A short description of the service

    //      IMPORTANT:
    //       Do NOT return diagnosis codes (ICD codes) or lists of conditions.
    //       Only return CPT-coded services or procedures that would typically be used to evaluate or manage the identified problems.

    //     ---

    //     ### Problems:
    //     ${problemsJson}

    //     ---

    //     ### Context Summaries:
    //     ${contextsJson}

    //     ---

    //     ### Output Format:
    //     {
    //       "displayName": "Cardiology Evaluation",
    //       "cptCode": "",
    //       "associatedProblem": {
    //         "id": "4dec93aa-446d-437f-9831-143bf0881cc2",
    //         "type": "Chronic",
    //         "status": "Unchanged",
    //         "isVerified": true,
    //         "isNew": false,
    //         "isChecked": false,
    //         "icdCode": "H10.1",
    //         "description": "Acute atopic conjunctivitis"
    //       },
    //       "modifiers": ["-RT", "-LT", "-52", "-GC", "-AS"],
    //       "description": "Acute atopic conjunctivitis"
    //     }

    //     Note:
    //      - Only return services that are medically relevant and helpful for further management or diagnosis of the problems mentioned.
    //      - Only return CPT codes not the diagnosis codes
    //      - Return the modifiers in the modifiers array
    //      - Return the associatedProblem object in the associatedProblem array
    //      - Only return valid JSON without any instructions or text
    //     `.trim();


    // return `You are a clinical coding copilot who specializes in CPT®/HCPCS selection for outpatient, ASC, and clinic settings.

    //   Your task:
    //   Translate the provided confirmed problems/diagnoses and encounter context into a ranked list of clinically appropriate, compliant CPT® (and HCPCS, when applicable) codes.
    //   Think step-by-step internally but DO NOT reveal your reasoning.
    //   Output only valid JSON matching the schema below.

    //   If key data are missing, explicitly include your assumptions, confidence level, and clarifying questions.

    //   ---

    //   ### Problems:
    //   ${problemsJson}

    //   ---

    //   ### Context Summaries:
    //   ${contextsJson}

    //   ---

    //   ### Output JSON Schema:

    //   {
    //     "ranked_codes": [
    //       {
    //         "rank": 1,
    //         "cpt_or_hcpcs": "string",
    //         "short_desc": "string",
    //         "category": "E/M | Procedure | Imaging | Lab | Therapy | Vaccine | Device/Supply | Other",
    //         "site_of_service": "Office | Outpatient Hospital | ASC | Telehealth | Home",
    //         "goals_addressed": ["Curative","Control","Symptom relief","Prevention"],
    //         "evidence_grade": "High | Moderate | Low",
    //         "guideline_refs": ["Society/Guideline Name, Year"],
    //         "required_modifiers": ["-25","-59","-51","-RT","-LT","-52","-GC","-AS"],
    //         "add_on_codes": ["string"],
    //         "bundling_notes": "string",
    //         "global_period": "0 | 10 | 90 | N/A",
    //         "medical_necessity_link": "ICD-10-CM code(s) or indication phrase",
    //         "mue_or_freq_limits": "string or null",
    //         "units": 1,
    //         "composite_score": 0,
    //         "rationale_short": "≤35 words why this code fits given goals, evidence, patient factors, risks, and SDM.",
    //         "confidence": "High | Moderate | Low",
    //         "linked_problem_ids": ["uuid-of-problem-1","uuid-of-problem-2"]
    //       }
    //     ]
    //   }

    //   ---

    //   ### Important:
    //   - Return only CPT/HCPCS codes (no ICD/diagnosis lists in the output except as “medical_necessity_link”).
    //   - Never fabricate codes, devices, or guidelines. If unsure, mark confidence: Low and add a clarifying question.
    //   - Prefer billable yet compliant solutions; if clinically ideal but bundled, include it in rejected_or_deprioritized with a clear note.
    //   - No text outside of the JSON.
    // `;
  }

  static getSoapNotePrompt(
    chiefComplaint: string,
    problems: Problem[],
    services: ProblemService[],
    medications: EncounterMedication[],
    allergies: Allergy[],
    medicalHistory: EncounterMedicalHistory[],
    familyHistory: EncounterFamilyHistory[],
    socialHistory: EncounterSocialHistory[],
    surgicalHistory: EncounterSurgicalHistory[],
    hpi: string,
    contexts: Context[],
    mainNotePrompt: Prompt,
    selectedTemplate: Template,
    showCharges: boolean = false
  ): string {
    medications = medications.filter((m) => !isCancelledEncounterMedication(m));
    const soapContexts = filterEncounterContextsForSoapNote(contexts);
    const formattedPrompt = `${mainNotePrompt?.content}

    Following are the special instructions for the SOAP note:
    ${selectedTemplate?.content}

    Here is the list of conversation summaries (context summaries) to base your SOAP note on:

    \n\n
    ${soapContexts.map((m) => `${m.title}\n${m.summary}`).join("\n")}
    \n\n

    List of Problems with ICD-10 Code and Description and unit:
    ${problems.map((p) => `${p.icdCode} - ${p.description}`).join("\n")}
      List of CPTs with modifiers and units:
    ${services
      .map(
        (s) =>
          `${s.description}, Modifiers: ${s.modifiers?.join() || ""}, Units: ${
            s.unit || ""
          }`
      )
      .join("\n")}
    
    ${
      medications.length === 0
        ? ""
        : "Here is the list of medications:\n" +
        medications.map((m) => `${m.name}`).join("\n")
    }
    ${
      allergies.length === 0
        ? ""
        : "Here is the list of allergies:\n" +
        allergies.map((alg) => alg.allergy).join("\n")
    }
    ${
      !medicalHistory?.length
        ? ""
        : "Here is the list of patient's medical history:\n" +
        medicalHistory.map((m) => `${m.history}`).join("\n")
    }
    ${
      !surgicalHistory?.length
        ? ""
        : "Here is the list of patient's surgical history:\n" +
        surgicalHistory.map((m) => `${m.history}`).join("\n")
    }
    ${
      !familyHistory?.length
        ? ""
        : "Here is the list of patient's family history:\n" +
        familyHistory.map((m) => `${m.history}`).join("\n")
    }
    ${
      !socialHistory?.length
        ? ""
        : "Here is the list of patient's social history:\n" +
        socialHistory.map((m) => `${m.history}`).join("\n")
    }
      Here is the HPI (History of Present Illness):
    ${hpi}


    ${showCharges ? `
      <b>PROBLEMS/DIAGNOSES:</b>
      ${problems.map((p) => `• ${p.icdCode} - ${p.description}`).join("\n    ")}
      
      <b>SERVICES/PROCEDURES:</b>
      ${services.map((s) => `• ${s.description}${s.modifiers?.length ? ` (Modifiers: ${s.modifiers.join(", ")})` : ''}${s.unit ? ` - Units: ${s.unit}` : ''}`).join("\n    ")}
      ` : ''}

      

    Use the following format exactly:

    ${selectedTemplate?.htmlContent}

    ---

    Be creative, but keep the content grounded and professional for medical documentation purposes.
    `;

    return formattedPrompt;
  }

  static transformQuestions(rawQuestions: Record<string, any[]>): Record<
    string,
    {
      id: string;
      question: string;
      category: string;
      comment: string;
      isSelected: 'Positive' | 'Negative' | 'None';
      code_system: string[];
      codes: Record<string, string>;
      inputType?: string;
      isMultiselect?: boolean;
      inputOptions?: any[];
      placeholder?: string;
      relevance?: string;
      purpose?: string;
      questionType?: string;
    }[]
  > {
    // Add defensive check for null/undefined input
    if (!rawQuestions || typeof rawQuestions !== 'object') {
      console.warn('Utils.transformQuestions: Invalid input, expected object but got:', rawQuestions);
      return {};
    }

    const structuredQuestions: Record<
      string,
      {
        id: string;
        question: string;
        category: string;
        comment: string;
        isSelected: 'Positive' | 'Negative' | 'None';
        code_system: string[];
        codes: Record<string, string>;
        inputType?: string;
        isMultiselect?: boolean;
        inputOptions?: any[];
        placeholder?: string;
        relevance?: string;
        purpose?: string;
        questionType?: string;
      }[]
    > = {};

    for (const [tag, questions] of Object.entries(rawQuestions)) {
      // Add defensive check for questions array
      if (!Array.isArray(questions)) {
        console.warn(`Utils.transformQuestions: Invalid questions array for tag "${tag}":`, questions);
        continue;
      }

      structuredQuestions[tag] = questions.map((q, i) => ({
        id: null,
        question: q.question || '',
        category: q.category || '',
        comment: "",
        isSelected: 'None',
        code_system: q.code_system || [],
        codes: q.codes || {},
        inputType: q.inputType || undefined,
        isMultiselect: q.isMultiselect || false,
        inputOptions: q.inputOptions || [],
        placeholder: q.placeholder || undefined,
        relevance: q.relevance || undefined,
        purpose: q.purpose || undefined,
        questionType: q.questionType || undefined,
      }));
    }

    return structuredQuestions;
  }

  static composeEncounterContextSummaryPrompt(
    chiefComplaint: string,
    problems: Problem[],
    services: ProblemService[],
    medications: EncounterMedication[],
    allergies: Allergy[],
    medicalHistory: EncounterMedicalHistory[],
    familyHistory: EncounterFamilyHistory[],
    socialHistory: EncounterSocialHistory[],
    surgicalHistory: EncounterSurgicalHistory[],
    hpi: string
  ): string {
    medications = medications.filter((m) => !isCancelledEncounterMedication(m));
    return `you are a medical documentation assistant.
    Your task is to carefully analyze the information provided below and produce a clean, clinically relevant summary.
    Please ensure the following:
    Use the following structured patient information to generate the summary.
    List of Problems with ICD-10 Code and Description and unit:
    ${problems.map((p) => `${p.icdCode} - ${p.description}`).join("\n")}
        List of CPTs with modifiers and units:
      ${services
      .map(
        (s) =>
          `${s.description}, Modifiers: ${
            s.modifiers?.join() || ""
          }, Units: ${s.unit || ""}`
      )
      .join("\n")}
      ${
      medications.length === 0
        ? ""
        : "Here is the list of medications:\n" +
        medications.map((m) => `${m.name}`).join("\n")
    }
      ${
      allergies.length === 0
        ? ""
        : "Here is the list of allergies:\n" +
        allergies.map((alg) => alg.allergy).join("\n")
    }
      ${
      !medicalHistory?.length
        ? ""
        : "Here is the list of patient's medical history:\n" +
        medicalHistory.map((m) => `${m.history}`).join("\n")
    }
      ${
      !surgicalHistory?.length
        ? ""
        : "Here is the list of patient's surgical history:\n" +
        surgicalHistory.map((m) => `${m.history}`).join("\n")
    }
      ${
      !familyHistory?.length
        ? ""
        : "Here is the list of patient's family history:\n" +
        familyHistory.map((m) => `${m.history}`).join("\n")
    }
      ${
      !socialHistory?.length
        ? ""
        : "Here is the list of patient's social history:\n" +
        socialHistory.map((m) => `${m.history}`).join("\n")
    }
        Here is the HPI (History of Present Illness):
      ${hpi}

      Your Task:
      1. Create a summary.
      2. This summary will be used to help populate the relevant clinical documentation template, so accuracy and clarity are critical.
      3. Do not include PII (Personal identifiable information)
      4. Do not return words like html, \`\`\`html etc

      Only output the medically meaningful content`;
  }

  static getTinyMCEConfig(
    screenHeight: number,
    mode: string = "wrap",
    menubar: boolean = false,
    toolbar: boolean = false,
    readonly: boolean = true,
  ): any {
    return {
      skin_url: "/assets/tinymce/skins/ui/oxide",
      theme_url: "/assets/tinymce/themes/silver/theme.min.js",
      content_css: "/assets/tinymce/skins/content/default/content.min.css",
      icons_url: "/assets/tinymce/icons/default/icons.min.js",
      external_plugins: {
        advlist: "/assets/tinymce/plugins/advlist/plugin.min.js",
        autolink: "/assets/tinymce/plugins/autolink/plugin.min.js",
        lists: "/assets/tinymce/plugins/lists/plugin.min.js",
        link: "/assets/tinymce/plugins/link/plugin.min.js",
        image: "/assets/tinymce/plugins/image/plugin.min.js",
        charmap: "/assets/tinymce/plugins/charmap/plugin.min.js",
        print: "/assets/tinymce/plugins/print/plugin.min.js",
        preview: "/assets/tinymce/plugins/preview/plugin.min.js",
        anchor: "/assets/tinymce/plugins/anchor/plugin.min.js",
        hr: "/assets/tinymce/plugins/hr/plugin.min.js",
        pagebreak: "/assets/tinymce/plugins/pagebreak/plugin.min.js",
        spellchecker: "/assets/tinymce/plugins/spellchecker/plugin.min.js",
        searchreplace: "/assets/tinymce/plugins/searchreplace/plugin.min.js",
        wordcount: "/assets/tinymce/plugins/wordcount/plugin.min.js",
        visualblocks: "/assets/tinymce/plugins/visualblocks/plugin.min.js",
        visualchars: "/assets/tinymce/plugins/visualchars/plugin.min.js",
        code: "/assets/tinymce/plugins/code/plugin.min.js",
        fullscreen: "/assets/tinymce/plugins/fullscreen/plugin.min.js",
        insertdatetime: "/assets/tinymce/plugins/insertdatetime/plugin.min.js",
        media: "/assets/tinymce/plugins/media/plugin.min.js",
        nonbreaking: "/assets/tinymce/plugins/nonbreaking/plugin.min.js",
        save: "/assets/tinymce/plugins/save/plugin.min.js",
        table: "/assets/tinymce/plugins/table/plugin.min.js",
        contextmenu: "/assets/tinymce/plugins/contextmenu/plugin.min.js",
        directionality: "/assets/tinymce/plugins/directionality/plugin.min.js",
        emoticons: "/assets/tinymce/plugins/emoticons/plugin.min.js",
        template: "/assets/tinymce/plugins/template/plugin.min.js",
        paste: "/assets/tinymce/plugins/paste/plugin.min.js",
        textcolor: "/assets/tinymce/plugins/textcolor/plugin.min.js",
        quickbars: "/assets/tinymce/plugins/quickbars/plugin.min.js",
      },
      statusbar: false,
      promotion: false,
      width: "100%",
      toolbar_mode: mode,
      readonly: readonly,
      height: (screenHeight === 1 ? 'calc(100vh - 345px)' : screenHeight === 0 ? 'calc(100vh - 290px)' : screenHeight),
      menubar: menubar,
      plugins: [
        "advlist autolink link image lists charmap print preview hr anchor pagebreak spellchecker",
        "searchreplace wordcount visualblocks visualchars code fullscreen insertdatetime media nonbreaking",
        "save table contextmenu directionality emoticons template paste textcolor quickbars",
      ],
      toolbar: toolbar
        ? "bold italic underline | forecolor backcolor | alignleft aligncenter alignright alignjustify | bullist numlist outdent indent | customChecklist"
        : false,
      style_formats: [
        { title: "Bold text", inline: "b" },
        { title: "Red text", inline: "span", styles: { color: "#ff0000" } },
        { title: "Red header", block: "h1", styles: { color: "#ff0000" } },
        { title: "Example 1", inline: "span", classes: "example1" },
        { title: "Example 2", inline: "span", classes: "example2" },
        { title: "Table styles" },
        { title: "Table row 1", selector: "tr", classes: "tablerow1" },
      ],

      // Add vertical scroll to content body
      content_style: `
        body {
          max-height: 200px;
          overflow-y: auto;
          box-sizing: border-box;
          padding-right: 20px;
        }
        
        p {
          padding-right: 20px;
        }

        /* Make scrollbar thin - always present but invisible by default */
        body::-webkit-scrollbar {
          width: 6px;
        }

        body::-webkit-scrollbar-thumb {
          background-color: #d1d1d1;
          border-radius: 3px;
          opacity: 0;
          transition: opacity 0.2s;
        }

        body::-webkit-scrollbar-track {
          background: transparent;
          opacity: 0;
          transition: opacity 0.2s;
        }
        
        body:hover::-webkit-scrollbar-thumb {
          opacity: 1;
        }
        
        body:hover::-webkit-scrollbar-track {
          opacity: 1;
          background: #f5f5f5;
        }
        
        * {
          scrollbar-width: thin;
          scrollbar-color: rgba(209, 209, 209, 0) transparent;
        }
        
        body:hover * {
          scrollbar-color: rgba(209, 209, 209, 1) rgba(245, 245, 245, 1);
        }

        /* Checklist item styles */
        .checklist-item {
          display: flex !important;
          align-items: center !important;
          gap: 8px !important;
          margin: 2px 0 !important;
          padding: 0 !important;
          line-height: 1.4 !important;
        }

        .checklist-item input[type="checkbox"],
        .checklist-checkbox {
          width: 16px !important;
          height: 16px !important;
          min-width: 16px !important;
          cursor: pointer !important;
          accent-color: #6366f1 !important;
          flex-shrink: 0 !important;
          margin: 0 !important;
          padding: 0 !important;
        }

        .checklist-item input[type="checkbox"]:checked + label,
        .checklist-item input[type="checkbox"]:checked + span,
        .checklist-item input[type="checkbox"][checked] + label,
        .checklist-item input[type="checkbox"][checked] + span {
          text-decoration: line-through;
          color: #9ca3af;
        }

        .checklist-item label,
        .checklist-item span {
          flex: 1 !important;
          line-height: 1.4 !important;
          cursor: pointer !important;
          margin: 0 !important;
          padding: 0 !important;
        }
      `,

      // Disable TinyMCE context menu to allow native browser menu with full paste support
      contextmenu: false,

      // Allow checkbox elements with all attributes
      extended_valid_elements: 'input[type|style|class|checked|id|name],label[for|style|class],div[class|style|id]',
      valid_children: '+p[input|label],+div[input|label],+span[input]',
      // Preserve all attributes including checked
      entity_encoding: 'raw',
      
      // Paste plugin configuration
      paste_as_text: false,
      paste_data_images: true,
      paste_enable_default_filters: true,
      paste_block_drop: false,

      // Smart paste from Word documents
      paste_word_valid_elements: "b,strong,i,em,h1,h2,h3,h4,h5,h6,p,ul,ol,li,a[href],table,tr,td,th,tbody,thead,tfoot,span",
      paste_retain_style_properties: "color font-size",
      paste_remove_styles_if_webkit: true,

      // Allow all keyboard shortcuts including paste
      browser_spellcheck: true,

      // Also apply scroll via setup if needed
      setup: (editor: any) => {
        // Helper to create a single checkbox item
        const createCheckboxItem = (text: string) => {
          const uniqueId = 'chk-' + Date.now() + '-' + Math.random().toString(36).substr(2, 9);
          return `<div class="checklist-item" style="display: flex; align-items: center; gap: 8px; margin: 2px 0; padding: 0;"><input type="checkbox" id="${uniqueId}" class="checklist-checkbox" style="width: 16px; height: 16px; min-width: 16px; cursor: pointer; accent-color: #6366f1; margin: 0;" /><label for="${uniqueId}" style="flex: 1; cursor: pointer; margin: 0; padding: 0;">${text.trim() || 'Checklist item'}</label></div>`;
        };

        // Register custom checklist button
        editor.ui.registry.addButton('customChecklist', {
          icon: 'checklist',
          tooltip: 'Insert Checklist Item',
          onAction: () => {
            const selectedText = editor.selection.getContent({ format: 'text' });
            
            if (selectedText && selectedText.trim()) {
              // Split by newlines, filter empty lines, create checkbox for each
              const lines = selectedText.split(/[\r\n]+/).filter((line: string) => line.trim());
              
              if (lines.length > 1) {
                // Multiple lines - create a checkbox for each line
                const checkboxes = lines.map((line: string) => createCheckboxItem(line)).join('');
                editor.selection.setContent(checkboxes);
              } else {
                // Single line or no newlines
                editor.selection.setContent(createCheckboxItem(selectedText));
              }
            } else {
              // No selection - insert single checkbox
              editor.insertContent(createCheckboxItem('Checklist item'));
            }
          }
        });

        // Register checklist menu item for context menu
        editor.ui.registry.addMenuItem('insertChecklist', {
          text: 'Insert Checklist',
          icon: 'checklist',
          onAction: () => {
            editor.insertContent(createCheckboxItem('Checklist item'));
          }
        });

        editor.on("init", () => {
          // Set readonly state if the method exists and readonly is true
          if (readonly && typeof editor.setReadOnly === 'function') {
            try {
              editor.setReadOnly(readonly);
            } catch (error) {
              // If setReadOnly fails, fall back to setting the body contentEditable
              const body = editor.getBody();
              if (body) {
                body.contentEditable = 'false';
              }
            }
          }
          const body = editor.getBody();
          if (body) {
            body.style.maxHeight = "200px";
            body.style.padding = "0px";
            body.style.marginTop = "0px";
            body.style.overflowY = "auto";
            body.style.color = "#8A8499";
          }

          // Add custom context menu with paste functionality
          editor.on("contextmenu", (e: any) => {
            // Allow native browser context menu which has paste
            // This ensures paste works properly
            e.stopPropagation();
          });

        });

        // Helper function to update checkbox state
        const updateCheckboxState = (checkbox: any) => {
          setTimeout(() => {
            if (checkbox.checked) {
              checkbox.setAttribute('checked', 'checked');
            } else {
              checkbox.removeAttribute('checked');
            }
            // Mark editor as dirty and fire change event
            editor.setDirty(true);
            editor.fire('change');
            editor.save(); // Force save to model
          }, 10);
        };

        // Handle checkbox click to persist checked state in HTML
        editor.on('click', (e: any) => {
          const target = e.target;
          
          // Direct checkbox click
          if (target && target.nodeName === 'INPUT' && target.type === 'checkbox') {
            updateCheckboxState(target);
          }
          
          // Label click - find associated checkbox
          if (target && target.nodeName === 'LABEL') {
            const forId = target.getAttribute('for');
            if (forId) {
              const checkbox = editor.getBody().querySelector('#' + forId);
              if (checkbox) {
                updateCheckboxState(checkbox);
              }
            }
          }
        });

        // Also handle via mouseup for better reliability
        editor.on('mouseup', (e: any) => {
          const target = e.target;
          if (target && target.nodeName === 'INPUT' && target.type === 'checkbox') {
            updateCheckboxState(target);
          }
        });
        
        // Handle change event on inputs
        editor.on('change', () => {
          // Sync all checkbox states when content changes
          const checkboxes = editor.getBody().querySelectorAll('input[type="checkbox"]');
          checkboxes.forEach((cb: any) => {
            if (cb.checked && !cb.hasAttribute('checked')) {
              cb.setAttribute('checked', 'checked');
            } else if (!cb.checked && cb.hasAttribute('checked')) {
              cb.removeAttribute('checked');
            }
          });
        });

        // Add custom paste menu item
        editor.ui.registry.addMenuItem('custompaste', {
          text: 'Paste',
          icon: 'paste',
          onAction: () => {
            // Try to paste from clipboard
            if (navigator.clipboard && navigator.clipboard.readText) {
              navigator.clipboard.readText().then((text) => {
                editor.insertContent(text);
              }).catch(() => {
                // Fallback: show message to use Ctrl+V
                editor.notificationManager.open({
                  text: 'Please use Ctrl+V (Cmd+V on Mac) to paste',
                  type: 'info',
                  timeout: 3000
                });
              });
            } else {
              // Focus editor and let user paste with keyboard
              editor.focus();
              editor.notificationManager.open({
                text: 'Please use Ctrl+V (Cmd+V on Mac) to paste',
                type: 'info',
                timeout: 3000
              });
            }
          }
        });
      },
    };
  }

  /**
   * Get prompt for ICD/CPT validation and Assessment Summary generation
   * This prompt validates ICD-10-CM and CPT/HCPCS codes and generates a clinical assessment summary
   */
  /**
   * Build validation prompt for encounter data validation.
   * If templateContent is provided (e.g. from "Soap Note Validation" prompt in cache), replaces {{input_json}} and {{input}} with the input JSON and returns it; otherwise uses the built-in prompt.
   * @param inputJson - The validation input JSON object
   * @param templateContent - Optional prompt template from API (must contain {{input_json}} or {{input}} placeholder)
   * @returns The validation prompt string
   */
  static buildValidationPrompt(inputJson: any, templateContent?: string): string {
    const input = JSON.stringify(inputJson, null, 2);
    if (templateContent != null && String(templateContent).trim().length > 0) {
      return String(templateContent)
        .replace(/\{\{input_json\}\}/g, input)
        .replace(/\{\{input\}\}/g, input);
    }

    // const basePrompt = `You are a meticulous clinical coding and documentation expert.

    //   Your responsibilities in this prompt:

    //   - Validate ICD-10-CM problems.
    //   - Validate CPT/HCPCS/E/M services.
    //   - Identify missing or insufficient clinical context.
    //   - Suggest additional context when needed.
    //   - Provide rationale tied directly to ICD-10-CM or CPT/NCCI guidelines.

    //   Strict Restrictions:
    //   - Do NOT generate an assessment summary.
    //   - Do NOT invent, assume, or infer undocumented findings.
    //   - Only evaluate what is explicitly present in the input.
    //   - Your output MUST be fully deterministic. The same input must always produce the same output.

    //   IMPORTANT:
    //   - Treat the ASSESSMENT SUMMARY block as authoritative clinical documentation ONLY for clinical findings.  
    //     It does NOT satisfy coding requirements by itself.  
    //     You must still evaluate whether each ICD-10-CM code and CPT/HCPCS/E/M service has explicit supporting 
    //     evidence, required specificity, and required modifiers.
    //   - Do NOT suggest new procedures, diagnostics, or treatments when the ASSESSMENT SUMMARY already documents 
    //     them. Only request missing discrete coding elements (laterality, modifier, measurement, etc.).
    //   - Return ONLY valid JSON. Do not include markdown code blocks, explanations, or any text outside the JSON structure.
    //   - The JSON must be parseable and complete.


    //   - Return ONLY valid JSON.  
    //   - Do NOT include markdown, explanations, or any text outside the JSON.  
    //   - The JSON must be parseable and complete.  
    //   - Use null for missing optional values.  
    //   - Boolean values must be true or false.  
    //   - Arrays should be [] not null.  
    //   - Do not repeat any validation row.

    //   Coding Rules to Follow:
    //   - ICD-10-CM specificity: laterality, episode of care, trimester, severity, manifestations, combination codes.
    //   - ICD-10-CM conventions & guidelines: Code First, Use Additional Code, Excludes1, Excludes2, Z-codes.
    //   - CPT/HCPCS rules: NCCI edits, bundling rules, add-on/base requirements, modifier rules (25, 59, 51, 26, TC).
    //   - Supervision and place-of-service rules.
    //   - Medical necessity rules.
    //   - E/M validation based on MDM or total time.

    //   ------------------------------------------------------------
    //   INPUT
    //   ------------------------------------------------------------
    //   ${JSON.stringify(inputJson, null, 2)}

    //   ------------------------------------------------------------
    //   TASK
    //   ------------------------------------------------------------
    //   Analyze the encounter and produce validation rows.

    //   VERY IMPORTANT STRUCTURAL RULES:

    //   1. Each missing or suggested context item MUST be returned as a separate row.  
    //     Never group multiple items in one row. Exactly ONE item per row.

    //   2. Output order must be stable and deterministic:
    //     - First: ICD validations (same order as input)
    //     - Then: CPT/HCPCS/E/M validations (same order as input)
    //     Inside each item, list:
    //       a. Missing rows  
    //       b. Suggested rows

    //   3. NEVER vary wording between runs.

    //   ------------------------------------------------------------
    //   OUTPUT FORMAT (valid JSON only)
    //   ------------------------------------------------------------
    //   {
    //     "results": [
    //       {
    //         "validation": "missing | suggested",
    //         "context": "single context item only",
    //         "rationale": "guideline-based explanation with no variation"
    //       }
    //     ],
    //     "can_proceed": true | false
    //   }

    //   ------------------------------------------------------------
    //   can_proceed RULE
    //   ------------------------------------------------------------
    //   - true  = No missing validations generated  
    //   - false = One or more missing validations exist
    //   `;

    // Version 2 prompt //
    const basePrompt = `
    You are a deterministic clinical coding and documentation validator.
  
    GOAL
    Validate ONLY the ICD-10-CM and CPT/HCPCS/E/M items that are explicitly provided in the input JSON. Identify documentation gaps required to support those items. Do not add new diagnoses or new billable services.
    
    ABSOLUTE RESTRICTIONS
    - Do NOT create or rewrite an assessment/plan/summary.
    - Do NOT invent, assume, or infer anything not explicitly documented.
    - Do NOT “upcode” or recommend different codes. Only validate the submitted codes/services and identify missing elements needed to support them.
    - Do NOT suggest additional procedures/tests/treatments beyond what is already documented.
    - Output MUST be fully deterministic: same input => same output. No variable phrasing.
    
    AUTHORITATIVE TEXT RULE
    - The ASSESSMENT_SUMMARY block is authoritative ONLY for clinical findings.
    - It does NOT, by itself, satisfy coding support. Each submitted code/service must still have explicit supporting documentation elsewhere in the note OR must be flagged as missing required support.
    
    INPUT
    ${JSON.stringify(inputJson, null, 2)}
    
    EXPECTED INPUT SHAPE (do not output this; use it to interpret input)
    - encounter.note_sections: object of text blocks (e.g., HPI, ROS, EXAM, MDM, ASSESSMENT_SUMMARY, PROCEDURES, TIME, ORDERS)
    - encounter.icd10_items: array of { code, description, user_label? }
    - encounter.cpt_items: array of { code, description, units?, modifiers?[], pos?, dx_links?[] }
    - encounter.em_item: null OR { code, method: "MDM"|"TIME", pos?, modifiers?[], documented_time_minutes? }
    
    DEFINITIONS (use exactly; do not vary)
    - SUPPORTED: explicit documentation supports the submitted item with required specificity/modifiers.
    - NOT_SUPPORTED: required supporting documentation is absent.
    - INSUFFICIENT_SPECIFICITY: documentation exists but missing specificity required by ICD-10-CM (e.g., laterality, acuity, stage).
    - REQUIRES_MODIFIER: service documented but missing required/appropriate modifier(s) to bill as submitted.
    - BUNDLING_RISK: documentation indicates possible NCCI bundling conflict for submitted combination (do not unbundle; only flag).
    - TIME_INCONSISTENT: time-based E/M submitted but required time elements are missing or inconsistent.
    - MDM_INCONSISTENT: MDM-based E/M submitted but required MDM elements are missing/insufficient.
    
    EVIDENCE RULES
    - Evidence must be exact verbatim snippets copied from the input text, with a JSON pointer path to the source block.
    - If no evidence exists, supported_by must be [].
    
    MISSING VS SUGGESTED CONTEXT
    - MISSING: discrete element REQUIRED to support the submitted code/service as billed (e.g., laterality, trimester, wound depth, modifier, total time).
    - SUGGESTED: discrete element NOT strictly required but improves defensibility (e.g., measurement detail, severity scale), and must not introduce new clinical facts.
    - Every missing/suggested element MUST be its own row: exactly one item per row.
    
    ORDERING (must be stable)
    1) ICD-10-CM validations in the same order as encounter.icd10_items
      - For each ICD item: emit all MISSING rows (in deterministic order), then all SUGGESTED rows (deterministic order), then the ITEM validation row.
    2) CPT/HCPCS validations in the same order as encounter.cpt_items
      - For each CPT item: emit all MISSING rows, then all SUGGESTED rows, then the ITEM validation row.
    3) E/M validation last (if encounter.em_item is not null)
      - Same missing/suggested then item validation pattern.
    
    GUIDELINE RATIONALE
    - Provide short rationale strings tied to: ICD-10-CM Official Guidelines (general), CPT rules, and NCCI policy.
    - Do not cite payers. Do not include long explanations. Use consistent phrasing.
    
    OUTPUT: RETURN ONLY VALID JSON (no markdown, no extra text)
    Must match this schema exactly:
    
    {
      "results": [
        {
          "validation": "missing | suggested",
          "context": "single context item only",
          "rationale": "guideline-based explanation with no variation"
        }
      ],
      "can_proceed": true | false
    }
    
    ------------------------------------------------------------
    can_proceed RULE
    ------------------------------------------------------------
    - true  = No missing validations generated  
    - false = One or more missing validations exist
    
    ADDITIONAL CONSTRAINTS
    - Arrays must be [] not null. Use null only where schema allows.
    - Do not duplicate rows.
    - Do not reference anything outside the input.
    - Use consistent, non-variable wording.
    `;

    return basePrompt;
  }

  static buildPlanSummaryPrompt(inputJson: any, options?: { format?: string; length?: string }, templateContent?: string): string {
    const formatVal = (options?.format?.trim() || 'paragraph').toLowerCase();
    const lengthVal = (options?.length?.trim() || 'standard').toLowerCase();

    const output = {
      format: formatVal === 'paragraph' || formatVal === 'bulleted' || formatVal === 'numbered' ? formatVal : 'paragraph',
      length: lengthVal === 'concise' || lengthVal === 'standard' || lengthVal === 'detailed' ? lengthVal : 'standard'
    };

    const inputJsonStr = JSON.stringify(inputJson, null, 2);
    if (templateContent != null && String(templateContent).trim().length > 0) {
      return String(templateContent)
        .replace(/\{\{input_json\}\}/g, inputJsonStr)
        .replace(/\{\{input\}\}/g, inputJsonStr)
        .replace(/\{\{output_length\}\}/g, output.length)
        .replace(/\{\{output_format\}\}/g, output.format);
    }

    const basePrompt = `
You are assisting a US medical provider drafting an audit-ready PLAN SUMMARY for an encounter.
 
Use ONLY the information I provide. Do NOT add or guess diagnoses, medications, doses, frequencies, durations, tests, referrals, procedures, devices, restrictions, follow-up intervals, or patient instructions that are not explicitly stated in the input. Do NOT contradict the confirmed problem list/ICD list or the confirmed services performed. If a needed plan element is missing, write “Not documented” rather than inventing it.

INPUT FORMAT (JSON):
${JSON.stringify(inputJson, null, 2)}

TASK:
Create a concise Plan Summary that is consistent with the above and supports medical necessity. Adapt to visit type (new vs established) and acuity based only on the input.
 
OUTPUT
- 1–2 sentences summarizing overall clinical rationale (severity/chronicity + functional impact + key objective/data).
- Then a tight bulleted Plan Summary where each bullet is written as: 
  [Action taken or planned] — [clinical justification from the input].
- Include “Today’s services:” as one bullet listing confirmed services performed with a one-line justification.
- Include only what is in the note; keep it executable and internally consistent.


OUTPUT PREFERENCES (presentation only — clinical content unchanged):
- Output Length: ${output.length}
- Output Format: ${output.format}

OUTPUT LENGTH (must follow exactly):
- concise: include all required elements but minimal wording; omit nice-to-haves (differential, extensive rationale)
- standard: add a bit more specificity + 1–2 supporting details
- detailed: add differential/clinical reasoning, relevant negatives, and fuller plan details. Make it more detailed and verbose.
 
OUTPUT FORMAT (must follow exactly):
- paragraph: output exactly ONE paragraph of plain text. No headings.
- bulleted: output HTML: <ul><li>...</li></ul>
- numbered: output HTML: <ol><li>...</li></ol>
 
Never output anything other than the requested length and format.

Paragraph format:
- ONE continuous paragraph
- Include all diagnoses when present
- No bullets, numbering, headings, or line breaks

Bulleted format:
- Return <ul> with one <li> per diagnosis
- If none exist, return a single <li> summarizing care plan
- 1–4 sentences per item
- No text before or after the list

Numbered format:
- Return <ol> with one <li> per diagnosis
- If none exist, return a single <li> summarizing care plan
- Do NOT manually write numbers
- No text before or after the list

HTML Only:
- Return valid HTML starting with <p>, <ul>, or <ol> and ending with the closing tag
- Do NOT wrap in Markdown, escape HTML, or append extra sections
- Do NOT wrap the response in Markdown code blocks.
- Do NOT use \`\`\`html or \`\`\` or any backticks.
- Do NOT add language labels.
- Do NOT escape HTML.
`;

    return basePrompt;

    /* OLD PROMPT (backup)
    const basePrompt = `
      You are a meticulous outpatient clinical documentation specialist and coder.
      Write one concise Plan summary paragraph for an outpatient visit.

      INPUT FORMAT (JSON):
      ${JSON.stringify(inputJson, null, 2)}

      Your task:
      Produce one paragraph outpatient plan summary that:
      - Addresses each confirmed problem in priority order: acute/unstable/high-risk first, then chronic uncontrolled, then stable chronic still managed today.
      - Does not omit any confirmed diagnosis.
      - Stays ICD-10-CM compliant in wording.
      - Does not add new diagnoses or speculate.
      - Uses required linkage language for combo/manifestation coding when describing care, e.g., "diabetes with neuropathy—optimize glycemic control and neuropathy care…", "hypertensive CKD with stage 3b CKD—BP control and renal monitoring…".
      - If related confirmed diagnoses exist, keep them linked in the plan consistent with the assessment.
      - Makes MEAT explicit through the care plan for every diagnosis using only provided details:
          M (Monitor): what will be tracked (labs, vitals, symptoms, home logs).
          E (Evaluate): what is being reassessed or interpreted (response to meds, trend review).
          A (Assess): brief rationale tying today's status to management need.
          T (Treat): medication changes/continuations, procedures, counseling, referrals.
      - In plan language, each problem must clearly show at least one MEAT element, and priority problems should show multiple elements (usually M+T, sometimes E/A).
      - Specifies actionable outpatient steps succinctly:
          • Med changes with purpose (start/stop/continue/adjust).
          • Diagnostic orders with clinical reason.
          • Referrals/education.
          • Safety/return precautions if provided.
          • Follow-up interval or next check if given.
      - Reflects CPT-supported services when relevant:
          mention tests/procedures performed or ordered if they support the plan (no billing talk).
      - Avoids contradictions or duplication:
          no lengthy medication lists; summarize changes/continuations.
          no inpatient-style orders unless explicitly provided.

      Formatting rules:
      - Output one paragraph only.
      - No bullets, no headings.
      - Use diagnosis names (codes optional).
      - Paraphrase; do not quote input.
    `;
    return basePrompt;
    */
  }

  /** #1
   * Build assessment summary prompt for generating assessment summary.
   * If templateContent is provided (e.g. from "Assessment Summary" prompt in cache), replaces {{input_json}}, {{output_length}}, {{output_format}} and returns it; otherwise uses the built-in prompt.
   * @param inputJson - The validation input JSON object
   * @param options - Optional format and length for output (paragraph/bulleted/numbered, concise/standard/detailed)
   * @param templateContent - Optional prompt template from API (placeholders: {{input_json}}, {{output_length}}, {{output_format}})
   * @returns The assessment summary prompt string
   */
  static buildAssessmentSummaryPrompt(inputJson: any, options?: { format?: string; length?: string }, templateContent?: string): string {
    const formatVal = (options?.format?.trim() || 'paragraph').toLowerCase();
    const lengthVal = (options?.length?.trim() || 'standard').toLowerCase();

    const output = {
      format: formatVal === 'paragraph' || formatVal === 'bulleted' || formatVal === 'numbered' ? formatVal : 'paragraph',
      length: lengthVal === 'concise' || lengthVal === 'standard' || lengthVal === 'detailed' ? lengthVal : 'standard'
    };

    const inputJsonStr = JSON.stringify(inputJson, null, 2);
    const linkedContextsStr = Array.isArray((inputJson as { linked_contexts?: string[] })?.linked_contexts)
      ? (inputJson as { linked_contexts: string[] }).linked_contexts.join('\n')
      : '';
    if (templateContent != null && String(templateContent).trim().length > 0) {
      return String(templateContent)
        .replace(/\{\{input_json\}\}/g, inputJsonStr)
        .replace(/\{\{input\}\}/g, inputJsonStr)
        .replace(/\{\{linked_contexts\}\}/g, linkedContextsStr)
        .replace(/\{\{output_length\}\}/g, output.length)
        .replace(/\{\{output_format\}\}/g, output.format);
    }

    const basePrompt = `
    You are assisting a US medical provider drafting an audit-ready clinical SUMMARY from an encounter.
    Use ONLY the information I provide. Do NOT add facts, diagnoses, exam findings, tests, treatments, timelines, or medications that are not explicitly stated. Do NOT contradict the supplied ICD list or plan. If something needed is missing, write “Not documented” rather than guessing.

    INPUT FORMAT (JSON):
    ${JSON.stringify(inputJson, null, 2)}

    TASK:
    1) Ask yourself: What is the visit type and the expected documentation emphasis for the target code (MDM vs time) based ONLY on the input.
    2) Produce a concise summary in the EXACT format I specify below. If I do not specify a format, output 1 short paragraph (6–10 sentences).

    CONTENT RULES (always follow):
    - Start with the primary diagnosis/problem and why it matters today (severity, chronicity, functional impact).
    - Include only the most relevant supporting subjective and objective findings.
    - Keep it clinically neutral, tight, and internally consistent.
    - Include an explicit “Problems addressed today” statement (status: new vs chronic; stable vs exacerbated/progressive; impact on function).

    OUTPUT PREFERENCES (presentation only — clinical content unchanged):
    - Output Length: ${output.length}
    - Output Format: ${output.format}

    OUTPUT LENGTH (must follow exactly):
    - concise: include all required elements but minimal wording; omit nice-to-haves (differential, extensive rationale)
    - standard: add a bit more specificity + 1–2 supporting details
    - detailed: add differential/clinical reasoning, relevant negatives, and fuller plan details. Make it more detailed and verbose.
    
    OUTPUT FORMAT (must follow exactly):
    - paragraph: output exactly ONE paragraph of plain text. No headings.
    - bulleted: output HTML: <ul><li>...</li></ul>
    - numbered: output HTML: <ol><li>...</li></ol>
    
    Never output anything other than the requested length and format.

    Rules:
    - Do NOT remove diagnoses because of concise mode
    - Only wording depth changes
    - Priority order MUST remain unchanged
    - Link related diagnoses when supported

    RENDERING RULES (STRICT STRUCTURE):
    - If NO confirmed diagnoses → generate a brief neutral clinical summary of the encounter using available symptoms, evaluation, or visit context.
    - NEVER output phrases like:
        "No clinically relevant assessment updates"
        "No clinically relevant plan updates"
        "Nothing to document"
        "Not documented"

    Paragraph format:
    - ONE continuous paragraph
    - Include ALL confirmed diagnoses
    - No bullets, numbering, headings, or line breaks

    Bulleted format:
    - Return <ul> with one <li> per diagnosis
    - If none exist, return a single <li> summarizing the visit
    - 1–3 sentences per item
    - No text before or after the list

    Numbered format:
    - Return <ol> with one <li> per diagnosis
    - If none exist, return a single <li> summarizing the visit
    - Do NOT manually write numbers
    - No text before or after the list

    HTML Only:
    - Return valid HTML starting with <p>, <ul>, or <ol> and ending with the closing tag
    - Do NOT wrap in Markdown, escape HTML, or append extra sections
    - Do NOT wrap the response in Markdown code blocks.
    - Do NOT use \`\`\`html or \`\`\` or any backticks.
    - Do NOT add language labels.
    - Do NOT escape HTML.
    `;

    return basePrompt;

    /* OLD PROMPT (backup)
    You are a meticulous outpatient clinical documentation specialist and coder. Write one concise assessment summary paragraph for an outpatient visit.

    INPUT FORMAT (JSON):
    ${JSON.stringify(inputJson, null, 2)}

    Your task:
    Produce one paragraph that:
    - Summarizes each confirmed problem in priority order.
      - First: acute/high-risk/unstable issues.
      - Then: chronic conditions that are uncontrolled or actively addressed.
      - Last: stable chronic conditions still MEAT-supported today.
    - Aligns strictly with ICD-10-CM guidelines.
    - Do not introduce new diagnoses.
    - Use required combination/manifestation linkages (with/due to/associated with/complicated by):
      Example: "Type 2 diabetes with neuropathy," "Hypertensive CKD with stage 3b CKD," "COPD with acute exacerbation."
    - If multiple related confirmed diagnoses exist, link explicitly when supported by the provided list.

    Provides explicit MEAT support for every diagnosis using only given data:
    - M (Monitor): objective/subjective measures tracked today (A1c, BP, eGFR, symptoms).
    - E (Evaluate): interpretation of findings or status (uncontrolled, improving, stable, stage/grade).
    - A (Assess): clinician judgment tying evidence to the diagnosis (severity/complication/risk).
    - T (Treat): management done/continued/adjusted today (meds, counseling, referrals, procedures).
    - MEAT may be implicit, but each problem must show at least one element, and priority problems typically show multiple (E/A/T).

    Additional rules:
    - Bind evidence succinctly to diagnosis and severity.
    - Use 1–2 key data points per problem.
    - Include stage/classification/complication when provided.
    - Avoid plan-only lists.
    - Integrate treatment references briefly (outpatient tone).
    - Mention CPT-reflected services only when they clarify evaluation (e.g., "EKG today showed…").
    - Maintain a concise, outpatient clinical tone (no bullets in final output, no headings).
    - If evidence is sparse: document conservatively (e.g., "history of… stable per available data; monitoring continued").

    Formatting rules:
    - Output one paragraph only.
    - Use diagnosis names (codes optional).
    - No copying input verbatim; paraphrase.
    */
  }

  /**
   * Check if appointment is scheduled with a non-provider resource
   * Non-provider resources have roleId !== 1
   * @param appointment - The appointment object
   * @param resources - Array of resources from application context
   * @returns boolean - true if scheduled with non-provider resource, false otherwise
   */
  static isScheduledWithNonProviderResource(appointment: any, resources: any[]): boolean {
    if (!appointment || !resources || resources.length === 0) {
      return false;
    }

    // Get scheduleResourceId from appointment (support nested encounterSchedule from API)
    const scheduleResourceId =
            appointment?.encounterSchedule?.[0]?.scheduleResourceId ||
            appointment?.scheduleResourceId ||
            appointment?.resourceId;

    if (!scheduleResourceId) {
      return false;
    }

    const scheduleIdStr = String(scheduleResourceId).trim();
    // Find the resource by id, name, or value (resources may be EnumerationItem-shaped with value/displayName)
    const resource = resources.find(r => {
      const id = r.id != null ? String(r.id).trim() : '';
      const name = (r.name ?? r.displayName ?? '').toString().trim();
      const value = r.value != null ? String(r.value).trim() : '';
      return id === scheduleIdStr || name === scheduleIdStr || value === scheduleIdStr ||
             id === scheduleResourceId || name === scheduleResourceId || value === scheduleResourceId;
    });

    if (!resource) {
      return false;
    }

    // Check if resource is a non-provider (roleId !== 1); handle roleId as number or string
    const roleId = resource.roleId != null ? Number(resource.roleId) : undefined;
    const isNonProvider = roleId !== 1;
    // console.log('[isScheduledWithNonProviderResource]', {
    //   encounterId: appointment?.id,
    //   scheduleResourceId,
    //   resourceRoleId: resource.roleId,
    //   isNonProvider
    // });
    return isNonProvider;
  }

  /**
   * Centralized Resource display name for header (referral-details and encounter page).
   * Returns "Name" or "Name, Title" (title in all caps). If title is missing, no comma and no title.
   * @param appointment - Appointment/encounter with encounterSchedule, providerId, providerName
   * @param resources - Application context resources (id, name, title)
   * @param providers - Application context providers (id, title)
   */
  static getResourceDisplayName(
    appointment: any,
    resources: any[] | null | undefined,
    providers: any[] | null | undefined
  ): string {
    if (!appointment) return '--';
    const scheduleResourceId = appointment?.encounterSchedule?.scheduleResourceId;
    let name = appointment?.providerName || '--';
    let title = '';

    const toDisplayTitle = (t: string | null | undefined): string => {
      const s = (t ?? '').trim();
      const upper = s.toUpperCase();
      if (!upper || upper === 'ADMIN') return '';
      return upper;
    };

    if (!scheduleResourceId || !resources?.length) {
      if (appointment?.providerId && providers?.length) {
        const provider = providers.find((p: any) => p.id === appointment.providerId);
        title = toDisplayTitle(provider?.title);
      }
      return title ? `${name}, ${title}` : name;
    }

    const resource = resources.find((r: any) =>
      r.id === scheduleResourceId || r.name === scheduleResourceId
    );

    if (resource) {
      name = resource.name || scheduleResourceId;
      title = toDisplayTitle(resource.title);
    } else {
      name = appointment?.providerName || '--';
      if (appointment?.providerId && providers?.length) {
        const provider = providers.find((p: any) => p.id === appointment.providerId);
        title = toDisplayTitle(provider?.title);
      }
    }

    return title ? `${name}, ${title}` : name;
  }

  /** `newOrEstablishedStatus` from encounter row (snake_case variants supported). */
  static readNewOrEstablishedStatus(
    appointment:
      | {
          newOrEstablishedStatus?: string;
          new_or_established_status?: string;
          NewOrEstablishedStatus?: string;
        }
      | null
      | undefined
  ): string | null {
    if (!appointment) {
      return null;
    }
    const value =
      appointment.newOrEstablishedStatus ??
      appointment.new_or_established_status ??
      appointment.NewOrEstablishedStatus;
    const trimmed = value != null ? String(value).trim() : "";
    return trimmed || null;
  }

  /** True when appointment `newOrEstablishedStatus` is Established (DB field only). */
  static isEstablishedFromNewOrEstablishedStatus(
    appointment:
      | {
          newOrEstablishedStatus?: string;
          new_or_established_status?: string;
          NewOrEstablishedStatus?: string;
        }
      | null
      | undefined
  ): boolean {
    const value = Utils.readNewOrEstablishedStatus(appointment);
    if (!value) {
      return false;
    }
    return value.replace(/_/g, " ").trim().toLowerCase() === "established";
  }

  /**
   * Same logic as `patient-status-badge` in appointment-patient-info:
   * 1. Use `newOrEstablishedStatus` on the appointment when the API provides it
   * 2. Else compute via encounter history + {@link getEncounterStatusNewPatientOrEstablished}
   */
  static isAppointmentPatientEstablished(
    appointment: any,
    encounters: any[] | null | undefined,
    resources: any[] | null | undefined,
    optionalCurrentAppointment?: any
  ): boolean {
    const fromDb = Utils.readNewOrEstablishedStatus(appointment);
    if (fromDb) {
      const normalized = fromDb.replace(/_/g, " ").trim().toLowerCase();
      if (normalized === "established") {
        return true;
      }
      if (normalized === "new patient" || normalized === "newpatient") {
        return false;
      }
    }

    const patientId =
      appointment?.patientId || appointment?.encountersPatient?.id;
    if (!appointment?.encounterDateTime || !patientId) {
      return Array.isArray(encounters) && encounters.length > 1;
    }
    if (!encounters?.length || !resources?.length) {
      return Array.isArray(encounters) && encounters.length > 1;
    }

    const firstEncounter = encounters[0];
    const cachedPatientId =
      firstEncounter?.patientId || firstEncounter?.encountersPatient?.id;
    if (cachedPatientId && cachedPatientId !== patientId) {
      return encounters.length > 1;
    }

    const status = Utils.getEncounterStatusNewPatientOrEstablished(
      encounters,
      appointment.encounterDateTime,
      resources,
      optionalCurrentAppointment
    );
    return status === EncounterStatus.Established;
  }

  /**
   * Get scheduleResourceId from an encounter/appointment (supports nested encounterSchedule array or object).
   */
  private static getEncounterScheduleResourceId(enc: any): string | number | undefined {
    const id =
      enc?.encounterSchedule?.[0]?.scheduleResourceId ??
      enc?.encounterSchedule?.scheduleResourceId ??
      enc?.scheduleResourceId ??
      enc?.resourceId;
    return id;
  }

  /**
   * Determine New Patient vs Established based on encounter history and provider scheduling.
   * Status is provider-specific: if the current encounter is scheduled with a provider and this is the
   * first time the patient has an encounter with that provider (all previous encounters were with
   * non-providers or with other providers), status is "New Patient". If the patient has had any
   * previous encounter with the same provider, status is "Established".
   * When the current encounter is with a non-provider, legacy rule applies: if ANY previous encounter
   * was with any provider, status is "Established"; otherwise "New Patient".
   * @param encounters - All encounters for the patient
   * @param currentOrReferenceDateTime - Current encounter date (or reference date); used to exclude future encounters
   * @param resources - Array of resources (must include roleId for provider check)
   * @param optionalCurrentAppointment - When adding a new appointment, pass it so its resource is included in the check
   * @returns EncounterStatus.NewPatient or EncounterStatus.Established
   */
  static getEncounterStatusNewPatientOrEstablished(
    encounters: any[],
    currentOrReferenceDateTime: string | Date,
    resources: any[],
    optionalCurrentAppointment?: any
  ): EncounterStatus {
    if (!resources || resources.length === 0) {
      return EncounterStatus.NewPatient;
    }

    // Use full date-time of current encounter so we exclude any future encounter (same day later time or later day)
    const refDateTime = typeof currentOrReferenceDateTime === 'string'
      ? DateTime.fromISO(currentOrReferenceDateTime)
      : DateTime.fromJSDate(currentOrReferenceDateTime);

    const encountersToCheck: any[] = [];

    if (encounters && encounters.length > 0) {
      for (const enc of encounters) {
        const encDateStr = enc?.encounterDateTime;
        if (!encDateStr) continue;
        const encDateTime = DateTime.fromISO(encDateStr);
        // Include only current and previous: exclude all future encounterDateTime vs current encounter
        if (encDateTime.toMillis() <= refDateTime.toMillis()) {
          encountersToCheck.push(enc);
        }
      }
    }

    if (optionalCurrentAppointment) {
      encountersToCheck.push(optionalCurrentAppointment);
    }

    if (encountersToCheck.length === 0) {
      return EncounterStatus.NewPatient;
    }

    // Identify current encounter: optional appointment if provided, else same date as ref, else most recent in list
    const currentEncounter =
      optionalCurrentAppointment ??
      encountersToCheck.find(
        e => e?.encounterDateTime && DateTime.fromISO(e.encounterDateTime).toMillis() === refDateTime.toMillis()
      ) ??
      [...encountersToCheck].sort(
        (a, b) => (DateTime.fromISO(b?.encounterDateTime ?? 0).toMillis() - DateTime.fromISO(a?.encounterDateTime ?? 0).toMillis())
      )[0];

    const currentResourceId = Utils.getEncounterScheduleResourceId(currentEncounter);
    const currentIsWithProvider = currentResourceId != null && !Utils.isScheduledWithNonProviderResource(currentEncounter, resources);

    const normalizeId = (id: string | number | undefined) => (id != null ? String(id).trim() : '');

    if (currentIsWithProvider && currentResourceId != null) {
      // Provider-specific rule: New Patient if first time with this provider (no previous encounter with same provider)
      const currentIdNorm = normalizeId(currentResourceId);
      const previousEncounters = encountersToCheck.filter(enc => enc !== currentEncounter);
      const hasPreviousWithSameProvider = previousEncounters.some(enc => {
        const rid = Utils.getEncounterScheduleResourceId(enc);
        if (rid == null) return false;
        const resource = resources.find(r => {
          const id = r.id != null ? String(r.id).trim() : '';
          const name = (r.name ?? r.displayName ?? '').toString().trim();
          const value = r.value != null ? String(r.value).trim() : '';
          return id === String(rid).trim() || name === String(rid).trim() || value === String(rid).trim() ||
            id === rid || name === rid || value === rid;
        });
        const roleId = resource?.roleId != null ? Number(resource.roleId) : undefined;
        const isProvider = roleId === 1;
        return isProvider && normalizeId(rid) === currentIdNorm;
      });
      return hasPreviousWithSameProvider ? EncounterStatus.Established : EncounterStatus.NewPatient;
    }

    // Current encounter is with non-provider: legacy rule — any previous with any provider → Established
    const previousEncounters = encountersToCheck.filter(enc => enc !== currentEncounter);
    const anyPreviousWithProvider = previousEncounters.some(enc => !Utils.isScheduledWithNonProviderResource(enc, resources));
    return anyPreviousWithProvider ? EncounterStatus.Established : EncounterStatus.NewPatient;
  }

  // #2
  static getIcdCptValidationAndAssessmentSummaryPrompt(): string {
    return `You are a meticulous clinical coding and documentation assistant for an ambulatory EMR. Your goals:

1. Validate confirmed Problems and the Plan (services/orders) against current ICD-10-CM and CPT/HCPCS coding conventions.
2. Draft a concise, clinically faithful Assessment Summary that aligns with ICD-10-CM Official Guidelines for Coding and Reporting.
3. Identify and clearly flag all issues with ICD and CPT/HCPCS coding, including missing specificity and bundling/edit conflicts.
4. Do not invent findings. Only use the provided encounter context.

General Rules:

ICD-10-CM Guidelines:
- Prefer highest specificity ICD-10-CM (4–7 characters), include laterality, episode of care, trimester, tobacco status, complications, manifestations/etiology sequencing as applicable.
- Apply the ICD-10-CM Official Guidelines including: Excludes1/Excludes2, "Code first"/"Use additional code", combination codes, sequela (late effects), external cause (when helpful), Z-codes (e.g., for screenings, status, history).
- Never change the clinical meaning. If something is unclear, call it out in queries_for_clinician.

CPT/HCPCS Guidelines:
- Check medical necessity, NCCI edits, bundling, add-on code dependencies, bilateral indicators, device/agent requirements, supervision requirements, global periods, appropriate modifiers (e.g., 25, 59, 51, 76, 77, 26, TC), POS consistency, and 2023/2024 E/M rules (MDM or total time).
- Never change the clinical meaning. If something is unclear, call it out in queries_for_clinician.

Assessment Summary Guidelines:
- Keep the human-readable Assessment Summary ≤ 180 words unless complex multi-problem encounters require more.
- Summarize each confirmed problem in priority order, tying key subjective/objective evidence succinctly to support diagnosis and severity.
- Include any required linkage phrases (e.g., "diabetes with neuropathy") to support combination/manifestation coding.

INPUT FORMAT (JSON):
{
  "patient": {
    "age": "{{age}}",
    "sex": "{{sex}}",
    "dob": "{{dob}}"
  },
  "vitals": "{{vitals_block}}",
  "subjective": "{{subjective_block}}",
  "objective": "{{objective_block}}",
  "confirmed_problems": [
    {
      "label": "{{problem_label}}",
      "icd_code": "{{icd10_if_present_or_null}}",
      "supporting_evidence_refs": ["{{optional_inline_refs}}"]
    }
    // ...more problems
  ],
  "plan_confirmed": {
    "services": [
      {
        "cpt": "{{cpt_or_hcpcs}}",
        "description": "{{service_desc}}",
        "modifiers": ["{{mods_or_empty}}"]
      }
      // ...more services
    ],
    "orders": [
      {
        "type": "{{lab|imaging|procedure|referral|med}}",
        "name": "{{order_name}}",
        "details": "{{dose|route|frequency|indication_if_med}}"
      }
      // ...more orders
    ],
    "em_level_claimed": "{{if_applicable_e_m_code}}"
  },
  "note_meta": {
    "date_of_service": "{{YYYY-MM-DD}}",
    "site_of_service": "{{ASC|OBL|Clinic|Telehealth}}",
    "payer": "{{if known}}",
    "specialty": "{{e.g., IR, Ortho, Vein, PrimaryCare}}"
  }
}

TASKS:

1. Validate Problems (ICD-10-CM):
   - For each problem, confirm adequacy/specificity of the ICD code. If missing or nonspecific, propose the most accurate specific code(s) and explain exactly what detail is missing (e.g., laterality, stage, trimester, severity).
   - Check sequencing rules (e.g., "code first" etiology before manifestation), combination codes, Excludes1 conflicts, and whether Z-codes/status/history are indicated.

2. Validate Plan (CPT/HCPCS/E/M):
   - For each billed service, verify bundle/edit conflicts (NCCI), add-on base code presence, modifier necessity and correctness, bilateral rules, device/agent documentation, supervision, medical necessity, and site/POS appropriateness.
   - For E/M (if present), check level against MDM or total time and medical necessity. If mis-leveled, suggest the correct level and rationale.

3. Produce a clinician-readable Assessment Summary aligned to ICD guidelines:
   - Summarize each confirmed problem in priority order, tying key subjective/objective evidence succinctly to support diagnosis and severity.
   - Include any required linkage phrases (e.g., "diabetes with neuropathy") to support combination/manifestation coding.
   - List any queries_for_clinician where missing details are blocking full specificity or correct CPT usage.

OUTPUT FORMAT (must be valid JSON):
{
  "assessment_summary": "string, ≤ ~180–220 words unless justified"
}

IMPORTANT:
- Return ONLY valid JSON. Do not include markdown code blocks, explanations, or any text outside the JSON structure.
- The JSON must be parseable and complete.
- All string fields should be properly escaped if they contain quotes or special characters.
- Use null for missing/optional values, not empty strings or undefined.
- Boolean values must be true or false (not strings).
- Arrays should be empty arrays [] if there are no items, not null.`;
  }
}

export enum PromptType {
  ValidateFile = 0,
  LastEncounter = 1,
  LastProcedure = 2,
  NonInvasive = 3,
  Ultrasound = 4,
  HPI = 5,
  FullMetabolic = 6,
  InformedConsent = 7,
  MedicationsAndAllergies = 8,
  ChiefComplaint = 9,
  PainCharacterizationQuestions = 11,
  ROS1Questions = 12,
  ROS2Questions = 13,
  HpiSummary = 10,
  HistoryQuestions = 15,
  RosSummary = 16,
}

export interface LabQuestion {
  id: string;
  question: string;
  dependentOn?: string;
  answer?: "Yes" | "No";
}

export interface cptCodesForPriorAuth {
  code: string;
  tooltip: string;
  section: CptCodeSection;
  isSelected?: boolean;
}

export enum MedicationStatus {
  Active = "active",
  InActive = "inactive",
  Continue = "continue",
  Discontinued = "discontinued",
  Refill = "refill",
}

export enum DayOfWeek {
  Monday = "Monday",
  Tuesday = "Tuesday",
  Wednesday = "Wednesday",
  Thursday = "Thursday",
  Friday = "Friday",
  Saturday = "Saturday",
  Sunday = "Sunday",
}

export enum MaritalStatus {
  Single = "Single",
  Married = "Married",
  Divorced = "Divorced",
  Widowed = "Widowed",
  Separated = "Separated",
}

export enum Gender {
  Male = "Male",
  Female = "Female",
}

export enum Ethnicity {
  AmericanIndianOrAlaskaNative = "American Indian or Alaska Native",
  Asian = "Asian",
  BlackOrAfricanAmerican = "Black or African American",
  White = "White",
  Unknown = "Unknown",
}

export enum Relationship {
  GrandFather = "Grand Father",
  GrandMother = "Grand Mother",
  Father = "Father",
  Mother = "Mother",
  Son = "Son",
  Daughter = "Daughter",
  Spouse = "Spouse",
  Sibling = "Sibling",
  Uncle = "Uncle",
  Aunt = "Aunt",
}

export enum PatientStatus {
  Admitted = "admitted",
}

export enum ViewMode {
  List = 1,
  Calendar,
}

export enum CalendarFilter {
  Day = 1,
  Week,
  Month,
}

export enum EncounterStatus {
  NewReferral = "New Referral",
  NewPatient = "New Patient",
  Established = "Established",
  Confirmed = "Confirmed",
  CheckedIn = "Checked In",
  AdditionalProcedure = "Additional Procedure",
  PendingEligibility = "Pending Eligibility",
  PendingAuth = "Pending Auth",
  NoAuthRequired = "No Auth Required",
  Authorized = "Authorized",
  Denied = "Denied",
  PendingConf = "Pending Conf",
  Scheduled = "Scheduled",
  PreProcedure = "PreProcedure",
  Procedure = "Procedure",
  PostProcedure = "PostProcedure",
  Discharge = "Discharge",
  Rescheduled = "Rescheduled",
  OpenProcedures = "Open Procedures",
  RetroAuth = "Retro Auth",
  Complete = "Complete",
  /** Same value as {@link Complete} — legacy code references. */
  Completed = Complete,
  Cancelled = "Cancelled",
  NoShow = "No Show",
  Waitlist = "Waitlist",
  ReadyForBilling = "Ready for Billing",
  Unconfirmed = "Unconfirmed",
  Encounter = "Encounter",
  Open = "Open",
  /** SOAP note generated; documentation in progress (stored on encounter_status.clinicalStatus). */
  Draft = "Draft",
  InComplete = "In Complete",
  Generated = "Generated",
}

export enum EncounterStatusV2 {
  //NewReferral = "New Referral",
  //NewPatient = "New Patient",
  //Established = "Established",
  Confirmed = "Confirmed",
  //CheckedIn = "Checked In",
  //AdditionalProcedure = "Additional Procedure",
  //PendingEligibility = "Pending Eligibility",
  //PendingAuth = "Pending Auth",
  //NoAuthRequired = "No Auth Required",
  //Authorized = "Authorized",
  //PendingConf = "Pending Conf",
  Scheduled = "Scheduled",
  //PreProcedure = "PreProcedure",
  //Procedure = "Procedure",
  //PostProcedure = "PostProcedure",
  //Discharge = "Discharge",
  Rescheduled = "Rescheduled",
  //OpenProcedures = "Open Procedures",
  RetroAuth = "Retro Auth",
  Completed = "Completed",
  Cancelled = "Cancelled",
  ReadyForBilling = "Ready for Billing",
  Unconfirmed = "Unconfirmed",
  //Encounter = "Encounter",
  Open = "Open",
  NoShow = "No Show",
  Waitlist = "Waitlist",
  Submitted = "Submitted",
  Unsubmitted = "Unsubmitted",
  SignReq = "Sign Req",
  //InProgress = "In Progress",
  //InComplete = "In Complete",
  //Generated = "Generated",
}

const INACTIVE_ENCOUNTER_STATUSES_FOR_DUPLICATE_SCHEDULE = new Set([
  EncounterStatus.Cancelled.toLowerCase(),
  EncounterStatus.NoShow.toLowerCase(),
  EncounterStatus.Rescheduled.toLowerCase(),
]);

/** Cancelled / no-show / rescheduled encounters must not block a new schedule on the same DOS. */
export function isEncounterInactiveForDuplicateSchedule(
  encounter:
    | { encounterStatus?: string; scheduleStatus?: string }
    | null
    | undefined
): boolean {
  if (!encounter) {
    return false;
  }
  const scheduleStatus = String(encounter.scheduleStatus ?? "")
    .trim()
    .toLowerCase();
  if (scheduleStatus === "cancelled") {
    return true;
  }
  const encounterStatus = String(encounter.encounterStatus ?? "")
    .trim()
    .toLowerCase();
  return INACTIVE_ENCOUNTER_STATUSES_FOR_DUPLICATE_SCHEDULE.has(encounterStatus);
}

export enum CheckboxState {
  Checked = 1,
  Indeterminate,
  Unchecked,
}

export enum VisitType {
  Clinic = "Clinic",
  Telehealth = "Telehealth",
  Procedure = "Procedure",
  Other = "Other",
}

export enum DurationList {
  FifteenMins = 15,
  HalfHour = 30,
  FourtyFiveMins = 45,
  OneHour = 60,
  NinetyMins = 90,
  TwoHours = 120,
  ThreeHours = 180,
}

export enum Region {
  Carotid = "Carotid",
  UpperExtremity = "Upper Extremity",
  Biliary = "Biliary",
  Renal = "Renal",
  LowerExtremity = "Lower Extremity",
}

export enum Area {
  Artery = "Artery",
  Vein = "Vein",
}

export enum Side {
  Left = "Left",
  Right = "Right",
}

export enum Location {
  DistalAbdominal = "Distal Abdominal",
  CommonIliac = "Common Iliac",
  ExternalIliac = "External Iliac",
  InternalIliac = "Internal Iliac",
  DeepFemoralProfunda = "Deep Femoral (Profunda)",
  CommonFemoral = "Common Femoral",
  ProxSuperficialFemoral = "Prox-Superficial Femoral",
  MidSuperficialFemoral = "Mid-Superficial Femoral",
  DisSuperficialFemoral = "Dis-Superficial Femoral",
  Popliteal = "Popliteal",
  AnteriorTibial = "Anterior Tibial",
  TibialPeronealTrunk = "Tibial Peroneal Trunk",
  PosteriorTibial = "Posterior Tibial",
  Peroneal = "Peroneal",
  DorsalisPedis = "Dorsalis Pedis",
  Planter = "Planter",
  Retroduodenal = "Retroduodenal",
  Gastroduodenal = "Gastroduodenal",
  RightBranchHepatic = "Right branch of the hepatic",
  Cystic = "Cystic",
  CeliacTrunk = "Celiac trunk",
  SuperiorMesenteric = "Superior Mesenteric",
  InferiorMesenteric = "Inferior mesenteric",
  Radial = "Radial",
  Ulnar = "Ulnar",
  Brachial = "Brachial",
  Brachialcephalic = "Brachialcephalic",
  Subclavian = "Subclavian",
}

export enum AccessSites {
  Brachial = "Brachial",
  Femoral = "Femoral",
  GreaterSaphenous = "Greater Saphenous",
  LesserSaphenous = "Lesser Saphenous",
  Accessory = "Accessory",
  Popliteal = "Popliteal",
  Radial = "Radial",
  Tibial = "Tibial",
}

export enum ProcedureType {
  Intervention = "Intervention",
  Diagnostic = "Diagnostic",
}

export enum CompReflex {
  Compression = "Compression",
  Reflex = "Reflex",
}

export enum NegativePositive {
  Negative = "Negative",
  Positive = "Positive",
}

export enum LabTest {
  BloodTest = "Blood Test",
  UrineTest = "Urine Test",
}

export enum DocStatus {
  Draft = "Draft",
  Submitted = "Submitted",
  Signed = "Signed",
}

export enum DocFilterType {
  ProcedureDoc = 0,
  OtherDoc,
}

export enum UploadDocumentType {
  Authorization = "Authorization",
  InsuranceInfo = "Insurance Info",
  Identification = "Identification",
  PreviousLabResults = "Previous lab results",
  IdentificationAndInsurance = "Identification & Insurance",
  SecondaryInsurance = "Secondary Insurance",
  OtherMiscellaneous = "Other/Miscellaneous",
  EKG = "EKG",
  PreviousPA = "Previous PA",
  ProblemList = "Problem List",
  MedicationList = "Medication list",
  MedicationOrders = "Medication Orders",
  Vitals = "Vitals",
  PayerNotice = "Payer Notice",
  DiagnosisNotes = "Diagnosis Notes",
  InfoReq = "Info Req",
  Diagnosis = "Diagnosis",
  LastEncounter = "Last Encounter",
  LastProcedure = "Last Procedure",
  Labs = "Labs",
  InformedConsent = "Informed Consent",
  UltrasoundFindings = "Ultrasound Findings",
  NonInvasive = "Non-Invasive ABI",
  Referral = "Referral",
  Problems = "Problems",
  Services = "Services",
  Diagnostics = "Diagnostics",
  OtherMisc = "Other/Miscellaneous",
}

export enum CptCodeType {
  Carotid = 1,
  Artery,
  Vein,
}

export enum CptCodeSection {
  Diagnostics = 1,
  Interventions,
}

export enum ChecklistMedicationType {
  BloodThinning = 1,
  Hypertension,
  Hyperlipidemia,
  Diabetes,
  Cilostazol,
}

export enum ReferralChecklistType {
  RutherfordClassification = 1,
  CeapScore,
  CessationProgram,
  WalkingProgram,
  Cilostazol,
  BloodThinningMedication,
  HypertensionMedications,
  HyperlipidemiaMedications,
  DiabetesMedications,
}

export enum ScheduledEventType {
  CheckIn = 1,
  Procedure,
  CheckOut,
}

export enum MedicationHoldStatus {
  OneDay = 1,
  TwoDays = 2,
  ThreeDays = 3,
  FourDays = 4,
  FiveDays = 5,
}

export enum ProgressbarStepper {
  PreProcedure,
  Procedure,
  PostProcedure,
  Discharge,
}

export enum ApiStatus {
  Pending = "Pending",
  Success = "Success",
  Failed = "Failed",
}

export enum EncounterApis {
  ChiefComplaint = "chiefComplaint",
  HpiQuestions = "hpiQuestions",
  HpiSummary = "hpiSummary",
  RecentAppointments = "recentAppointments",
  Encounter = "encounter",
  Problems = "problems",
  Contexts = "contexts",
  Services = "services",
  Medications = "medications",
  SoapNote = "soapNote",
  SocialHistory = "socialHistory",
  FamilyHistory = "familyHistory",
  MedicalHistory = "medicalHistory",
  SurgicalHistory = "surgicalHistory",
  RosSummary = "rosSummary",
  HpiSymptoms = "hpiSymptoms",
  GeneratingTriageQuestion = "generatingTriageQuestion",
  SuggestedServices = "suggestedServices",
  SuggestedProblems = "suggestedProblems",
}

export enum ReferralApis {
  Patient = "patient",
  Documents = "documents",
  PriorAuth = "priorAuth",
  Coverages = "coverages",
  ChecklistFlags = "checklistFlags",
  ChecklistQuestions = "checklistQuestions",
  ChecklistValues = "checklistValues",
  Labs = "labs",
  ChiefComplaint = "chiefComplaint",
  Activity = "activity",
  Allergies = "allergies",
  InitiatePreAnesthsiaSave = "initiatePreAnesthsiaSave",
  PreAnesthsiaSaveCompleted = "preAnesthsiaSaveCompleted",
  PreAnesthsiaSummary = "preAnesthsiaSummary",
}

export enum ProcedureRoomApis {
  ProcedureRoomContexts = "procedureRoomContexts",
  Patient = "patient",
  NeurovascularAssessment = "neurovascularAssessment",
  SnacksAndMeals = "snacksAndMeals",
  Vitals = "vitals",
  BloodGlucose = "bloodGlucose",
  DispenseSupplies = "dispenseSupplies",
  ModifiedAldreteScoring = "modifiedAldreteScoring",
  LeadEKG = "leadEKG",
  MedicationReconciliation = "medicationReconciliation",
  PreProcedureAssessment = "preProcedureAssessment",
  AdministerMedication = "administerMedication",
  AccessSite = "accessSite",
  Act = "act",
  ProcedureSummary = "procedureSummary",
  ProcedureTimeout = "procedureTimeout",
  ICDCodes = "icdCodes",
  CPTCodes = "cptCodes",
  Discharge = "discharge",
}

export enum OrderType {
  prior = "Prior",
  procedure = "Procedure",
  rx = "Rx",
}

export enum CPTCodes {
  EKG = "43522",
  Nursing = "99211",
  Chem8 = "43523",
  SedationInitial = "99152",
  SedationAdditional = "99153",
  LowOsmolarContrast = "Q9966",
}

export enum ProcedureDocuments {
  OperativeNote = "Operative Note",
  PreOpProtocol = "Pre-Op Protocol",
  PostOpProtocol = "Post-Op Protocol",
  DischargeOrder = "Discharge Order",
  DischargeInstructions = "Discharge Instructions",
  Consent = "Consent",
  MedicationOrder = "Medication Order",
  Chem8Order = "Chem 8 Order",
  EKG = "EKG",
  ProcedureSummary = "Procedure Summary	",
  Images = "Images",
}

export enum MedType {
  Medication = "Medication",
  Diagnosis = "Diagnosis",
  Allergies = "Allergies",
}

export enum HpiQuestionType {
  PainCharacterization = "Pain_Characterization",
  ROS = "Review_of_Systems",
  PastMedicalHistory = "Past_Medical_and_Surgical_History",
  FamilyHistory = "Family_History",
  SocialHistory = "Social_History",
}
